import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  RotateCw,
  Plus,
  Trash2,
  Edit,
  CheckCircle2,
  History,
  AlertTriangle,
  CalendarDays,
  Download,
  Upload,
} from 'lucide-react';
import {
  useForm,
  Controller,
  useWatch,
  useFieldArray,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import { z } from 'zod';

import { FormField, FormInput, FormSelect, FormTextarea } from '@/components/form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useCreateVersion, useSetActiveVersion, useDeleteVersion } from '@/hooks/useConfigurations';
import { useEmailConnectors } from '@/hooks/useEmailConnectors';
import { useSettings } from '@/hooks/useSettings';
import { useTelnyxNumbers } from '@/hooks/useTelnyxNumbers';
import {
  useTools,
  useCustomTools,
  useCreateCustomTool,
  useUpdateCustomTool,
  useDeleteCustomTool,
} from '@/hooks/useTools';
import { useTwilioNumbers } from '@/hooks/useTwilioNumbers';
import { useWorkflows } from '@/hooks/useWorkflows';
import { configurationsApi } from '@/lib/api/configurations.api';
import { toolsApi } from '@/lib/api/tools.api';
import { getEnv } from '@/lib/env';
import { scrollToFirstError, mergeApiErrors } from '@/lib/form-utils';
import { extractValidationErrors, extractErrorMessage } from '@/lib/utils';
import { useBrandingStore } from '@/stores/branding.store';
import { convertLocalTimeToUTC, convertUTCToLocalTime } from '@/utils/timezone';

import { GEMINI_VOICE_OPTIONS } from '@repo/shared-types';

import { TemplateCodeEditor } from '../workflow-builder/TemplateCodeEditor';

import { ConversationFlowPanel } from './agent-graph/ConversationFlowPanel';
import { CustomToolModal } from './CustomToolModal';
import { DeleteCustomToolDialog } from './DeleteCustomToolDialog';
import {
  GEMINI_38_VERTEX_OPTION,
  liveModelSelection,
  parseLiveModelSelection,
} from './gemini-live-model-selection';
import { MultiAgentSection } from './MultiAgentSection';
import { RagKnowledgeSection } from './RagKnowledgeSection';
import { ToolExecutionOverrides } from './ToolExecutionOverrides';
import { WidgetPreview } from './WidgetPreview';

import type { ConfigurationFormProps } from './types';
import type {
  UpdateConfigurationSetupDto,
  Tool,
  CustomToolDetail,
  ConfigurationVersion,
  TransferScheduleEntry,
  TransferTarget,
  EmailConnector,
  AgentGraph,
  WhatsappRoutingNumber,
} from '@repo/shared-types';

const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const TRANSFER_SCHEDULE_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const TRANSFER_SCHEDULE_DAY_LABEL: Record<(typeof TRANSFER_SCHEDULE_DAYS)[number], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const cloneDefaultTransferSchedule = (): TransferScheduleEntry[] =>
  TRANSFER_SCHEDULE_DAYS.map((day) => ({
    day,
    enabled: false,
    start_time: '09:00',
    end_time: '17:00',
  }));

const normalizeTransferScheduleForForm = (
  schedule?: TransferScheduleEntry[] | null,
): TransferScheduleEntry[] => {
  const defaults = cloneDefaultTransferSchedule();
  const byDay = new Map((schedule ?? []).map((entry) => [entry.day, entry]));

  return defaults.map((fallback) => {
    const existing = byDay.get(fallback.day);
    if (!existing) {
      return fallback;
    }

    return {
      day: fallback.day,
      enabled: Boolean(existing.enabled),
      start_time: TIME_REGEX.test(existing.start_time) ? existing.start_time : fallback.start_time,
      end_time: TIME_REGEX.test(existing.end_time) ? existing.end_time : fallback.end_time,
    };
  });
};

const transferTargetFormSchema = z.object({
  category_key: z
    .string()
    .trim()
    .min(1, 'Category key is required')
    .regex(/^[a-z][a-z0-9_]*$/, 'Category key must be snake_case (e.g., finance_sales)'),
  category_description: z.string().trim().min(2, 'Category description is required'),
  phone_number: z
    .string()
    .trim()
    .regex(E164_PHONE_REGEX, 'Must be a valid E.164 phone number (e.g., +441234567890)'),
  sms_enabled: z.boolean(),
  is_default: z.boolean(),
  notify_emails: z.array(z.string().email()),
  active: z.boolean(),
});

const createEmptyWhatsappNumber = (): WhatsappRoutingNumber => ({
  number: '',
  label: '',
});

// Old configs only ever had the single `whatsapp_number` field. Seed the new
// list with that legacy value so it shows up in the form instead of looking
// wiped, and so an unchanged save doesn't silently drop it.
const seedWhatsappNumbers = (
  numbers: WhatsappRoutingNumber[] | undefined | null,
  legacyNumber: string | undefined | null,
): WhatsappRoutingNumber[] => {
  if (numbers && numbers.length > 0) return numbers;
  return legacyNumber?.trim() ? [{ number: legacyNumber.trim(), label: '' }] : [];
};

const createEmptyTransferTarget = (): TransferTarget => ({
  category_key: '',
  category_description: '',
  phone_number: '',
  sms_enabled: false,
  is_default: false,
  notify_emails: [],
  active: true,
});

const transferScheduleFormSchema = z
  .array(
    z
      .object({
        day: z.enum(TRANSFER_SCHEDULE_DAYS),
        enabled: z.boolean(),
        start_time: z.string().regex(TIME_REGEX, 'Time must be in HH:MM format'),
        end_time: z.string().regex(TIME_REGEX, 'Time must be in HH:MM format'),
      })
      .refine(
        (entry) =>
          !entry.enabled ||
          parseTimeToMinutes(entry.start_time) < parseTimeToMinutes(entry.end_time),
        {
          message: 'End time must be after start time.',
          path: ['end_time'],
        },
      ),
  )
  .length(TRANSFER_SCHEDULE_DAYS.length)
  .refine(
    (entries) => {
      const uniqueDays = new Set(entries.map((entry) => entry.day));
      return uniqueDays.size === TRANSFER_SCHEDULE_DAYS.length;
    },
    {
      message: 'Schedule must contain each day once.',
    },
  );

const PROVIDER_OPTIONS = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI (GPT-Live)' },
] as const;

const GEMINI_MODEL_OPTIONS = [
  {
    value: 'gemini-3.8-live',
    label: 'Gemini 3.8 Live (AI Studio)',
    description: '128k context, low-latency voice, asynchronous tools',
  },
  {
    value: GEMINI_38_VERTEX_OPTION,
    label: 'Gemini 3.8 Live (Vertex)',
    description: 'Existing GCP credentials; European deployments use the EU endpoint',
  },
  {
    value: 'gemini-3.8-live-extended-thinking',
    label: 'Gemini 3.8 Live Extended Thinking (AI Studio)',
    description: 'Background reasoning, low/medium/high thinking, asynchronous tools',
  },
  {
    value: 'gemini-3.5-flash-live-preview',
    label: 'Gemini 3.5 Flash Live (Vertex)',
    description: '128k context, Vertex AI us-central1, early preview, thinking support',
  },
  {
    value: 'gemini-3.5-flash-lite-live-preview',
    label: 'Gemini 3.5 Flash-Lite Live (Vertex, new)',
    description: 'Lite variant: lower cost/latency, Vertex AI us-central1, thinking support',
  },
  {
    value: 'gemini-3.1-flash-live-preview',
    label: 'Gemini 3.1 Flash Live',
    description: '128k context, ultra-low latency, improved tonal understanding',
  },
  {
    value: 'gemini-3.1-flash-live-preview-04-2026',
    label: 'Gemini 3.1 Flash Live (Vertex)',
    description: '128k context, Vertex AI us-central1, native grounding',
  },
  {
    value: 'gemini-live-2.5-flash-native-audio',
    label: 'Gemini 2.5 Flash Native Audio',
    description: '100k context, best quality, auto language',
  },
  {
    value: 'gemini-2.0-flash-live-preview-04-09',
    label: 'Gemini 2.0 Flash Live (UK English)',
    description: '25k context, supports explicit en-GB',
  },
] as const;

const OPENAI_VOICE_OPTIONS = [
  { value: 'marin', label: 'Marin', description: 'Default GPT-Live voice' },
  { value: 'cedar', label: 'Cedar', description: 'Warm, natural' },
  { value: 'quartz', label: 'Quartz', description: 'GPT-Live voice' },
  { value: 'ripple', label: 'Ripple', description: 'GPT-Live voice' },
  { value: 'vesper', label: 'Vesper', description: 'GPT-Live voice' },
  { value: 'willow', label: 'Willow', description: 'GPT-Live voice' },
  { value: 'stone', label: 'Stone', description: 'GPT-Live voice' },
  { value: 'gleam', label: 'Gleam', description: 'GPT-Live voice' },
  { value: 'meridian', label: 'Meridian', description: 'GPT-Live voice' },
  { value: 'bossa', label: 'Bossa', description: 'GPT-Live voice' },
  { value: 'tempo', label: 'Tempo', description: 'GPT-Live voice' },
  { value: 'beacon', label: 'Beacon', description: 'GPT-Live voice' },
  { value: 'delta', label: 'Delta', description: 'GPT-Live voice' },
  { value: 'cinder', label: 'Cinder', description: 'GPT-Live voice' },
  { value: 'alloy', label: 'Alloy', description: 'Balanced, versatile' },
  { value: 'ash', label: 'Ash', description: 'Clear, direct' },
  { value: 'ballad', label: 'Ballad', description: 'Warm, melodic' },
  { value: 'coral', label: 'Coral', description: 'Bright, friendly' },
  { value: 'echo', label: 'Echo', description: 'Deeper' },
  { value: 'sage', label: 'Sage', description: 'Calm, measured' },
  { value: 'shimmer', label: 'Shimmer', description: 'Clear, bright' },
  { value: 'verse', label: 'Verse', description: 'Expressive' },
] as const;

const OPENAI_MODEL_OPTIONS = [
  {
    value: 'gpt-live-1',
    label: 'GPT-Live 1',
    description: 'Full-duplex voice model; tools run on the backend model below',
  },
  {
    value: 'gpt-4o-realtime-preview-2024-12-17',
    label: 'GPT-4o Realtime (legacy — runs as GPT-Live 1)',
    description: 'Old Realtime model; the backend runs this call on gpt-live-1',
  },
] as const;

const OPENAI_BACKEND_MODEL_OPTIONS = [
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (default)' },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
] as const;

const OPENAI_BACKEND_REASONING_OPTIONS = ['none', 'minimal', 'low', 'medium', 'high'] as const;

const DEFAULT_PROVIDER = 'gemini';
const DEFAULT_GEMINI_VOICE = 'Zephyr';
const DEFAULT_GEMINI_MODEL = 'gemini-live-2.5-flash-native-audio';
const DEFAULT_OPENAI_VOICE = 'marin';
const DEFAULT_OPENAI_MODEL = 'gpt-live-1';

const DEFAULT_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #333; line-height: 1.6; margin: 0; padding: 0; }
    .wrapper { max-width: 600px; margin: 0 auto; padding: 20px; }
    .content { padding: 0 0 20px; }
    .signature { border-top: 1px solid #e0e0e0; padding-top: 16px; margin-top: 16px; font-size: 12px; color: #666; }
    .disclaimer { border-top: 1px solid #e0e0e0; padding-top: 12px; margin-top: 24px; font-size: 10px; color: #999; line-height: 1.4; }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="content">
      {{content}}
    </div>
    <div class="signature">
      <strong>Your Name</strong><br>
      Your Company<br>
      <a href="tel:+441234567890">+44 1234 567890</a>
    </div>
    <div class="disclaimer">
      This email is confidential and intended for the addressee only.
      If you have received this email in error, please notify the sender immediately.
    </div>
  </div>
</body>
</html>`;

// Gemini 3.x live models share the same advanced-settings profile:
// thinking_level instead of thinking_budget, no affective dialog, no proactive audio.
const GEMINI_31_MODEL_PREFIX = 'gemini-3.1-flash-live-preview';
const GEMINI_38_LIVE = 'gemini-3.8-live';
const GEMINI_38_THINKING = 'gemini-3.8-live-extended-thinking';
const isGemini38Model = (model?: string): boolean =>
  model === GEMINI_38_LIVE || model === GEMINI_38_THINKING;
// 3.5 additionally rejects FunctionResponse.scheduling (tool_response_scheduling).
const GEMINI_35_MODEL_PREFIX = 'gemini-3.5-flash-live-preview';
// 3.5 Lite shares the 3.x thinking profile but DOES accept tool_response_scheduling.
const GEMINI_35_LITE_MODEL_PREFIX = 'gemini-3.5-flash-lite-live-preview';
const GEMINI_THINKING_LIVE_MODEL_PREFIXES = [
  GEMINI_31_MODEL_PREFIX,
  GEMINI_35_MODEL_PREFIX,
  GEMINI_35_LITE_MODEL_PREFIX,
] as const;
const GEMINI_25_NATIVE_MODEL_PREFIX = 'gemini-live-2.5-flash-native-audio';
const GEMINI_THINKING_LEVEL_OPTIONS = [
  { value: 'minimal', label: 'Minimal (lowest latency)' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High (deepest reasoning)' },
] as const;

const isGeminiThinkingLiveModel = (model?: string): boolean =>
  model === GEMINI_38_THINKING ||
  Boolean(model && GEMINI_THINKING_LIVE_MODEL_PREFIXES.some((prefix) => model.startsWith(prefix)));

const isGemini35LiveModel = (model?: string): boolean =>
  Boolean(model?.startsWith(GEMINI_35_MODEL_PREFIX));

const isGeminiNativeAudioModel = (model?: string): boolean =>
  Boolean(model?.startsWith(GEMINI_25_NATIVE_MODEL_PREFIX));

const ADVANCED_PLACEHOLDER_DEFAULT = `{
  "language_code": "en-GB",
  "temperature": 0.8,
  "thinking_budget": 512,
  "include_thoughts": false,
  "vad_disabled": false,
  "vad_start_sensitivity": "START_SENSITIVITY_LOW",
  "vad_end_sensitivity": "END_SENSITIVITY_HIGH",
  "prefix_padding_ms": 150,
  "silence_duration_ms": 300,
  "enable_affective_dialog": true,
  "proactive_audio": false,
  "compression_disabled": false,
  "trigger_tokens": 100000,
  "target_tokens": 80000
}`;

const ADVANCED_PLACEHOLDER_GEMINI_THINKING = `{
  "language_code": "en-GB",
  "temperature": 0.8,
  "thinking_level": "minimal",
  "include_thoughts": false,
  "vad_disabled": false,
  "vad_start_sensitivity": "START_SENSITIVITY_LOW",
  "vad_end_sensitivity": "END_SENSITIVITY_HIGH",
  "prefix_padding_ms": 150,
  "silence_duration_ms": 300,
  "compression_disabled": false,
  "trigger_tokens": 100000,
  "target_tokens": 80000
}`;

const ADVANCED_PLACEHOLDER_GEMINI_NATIVE = `{
  "temperature": 0.8,
  "thinking_budget": 512,
  "include_thoughts": false,
  "vad_disabled": false,
  "vad_start_sensitivity": "START_SENSITIVITY_LOW",
  "vad_end_sensitivity": "END_SENSITIVITY_HIGH",
  "prefix_padding_ms": 150,
  "silence_duration_ms": 300,
  "enable_affective_dialog": true,
  "proactive_audio": false,
  "compression_disabled": false,
  "trigger_tokens": 100000,
  "target_tokens": 80000
}`;

const parseAdvancedJson = (value?: string): Record<string, unknown> => {
  if (!value?.trim()) {
    return {};
  }

  const parsed = JSON.parse(value);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }

  throw new Error('Advanced configuration must be a JSON object');
};

const formatAdvancedJson = (
  advanced?: Record<string, unknown> | null,
  voiceName?: string,
  model?: string,
): string => {
  const merged: Record<string, unknown> = {
    ...(advanced ?? {}),
  };

  if (voiceName && !('voice_name' in merged)) {
    merged.voice_name = voiceName;
  }
  if (model && !('model' in merged)) {
    merged.model = model;
  }

  return JSON.stringify(merged, null, 2);
};

const parseAdvancedJsonSafe = (value?: string): Record<string, unknown> => {
  try {
    return parseAdvancedJson(value);
  } catch {
    return {};
  }
};

const mapThinkingBudgetToLevel = (value: unknown): 'minimal' | 'low' | 'medium' | 'high' => {
  if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
    return 'minimal';
  }
  if (value <= 512) {
    return 'low';
  }
  if (value <= 2048) {
    return 'medium';
  }
  return 'high';
};

const normalizeAdvancedForModel = (
  advanced: Record<string, unknown>,
  provider: 'gemini' | 'openai',
  model?: string,
): Record<string, unknown> => {
  if (provider !== 'gemini') {
    return advanced;
  }

  const normalized: Record<string, unknown> = { ...advanced };

  if (isGemini38Model(model)) {
    delete normalized.thinking_budget;
    delete normalized.include_thoughts;
    delete normalized.enable_affective_dialog;
    delete normalized.proactive_audio;
    if (model === GEMINI_38_THINKING) {
      const level = String(normalized.thinking_level ?? '')
        .trim()
        .toLowerCase();
      normalized.thinking_level = ['low', 'medium', 'high'].includes(level) ? level : 'low';
      delete normalized.tool_response_scheduling;
      normalized.tool_execution_behavior = 'non_blocking';
    } else {
      delete normalized.thinking_level;
    }
    return normalized;
  }

  if (isGeminiNativeAudioModel(model)) {
    delete normalized.language_code;
  }

  if (!isGeminiThinkingLiveModel(model)) {
    return normalized;
  }

  const rawThinkingLevel =
    typeof normalized.thinking_level === 'string' ? normalized.thinking_level : '';
  const allowedThinkingLevels = new Set(['minimal', 'low', 'medium', 'high']);

  normalized.thinking_level = allowedThinkingLevels.has(rawThinkingLevel)
    ? rawThinkingLevel
    : mapThinkingBudgetToLevel(normalized.thinking_budget);

  delete normalized.thinking_budget;
  delete normalized.enable_affective_dialog;
  delete normalized.proactive_audio;

  if (isGemini35LiveModel(model)) {
    delete normalized.tool_response_scheduling;
  }

  return normalized;
};

const getWelcomeMessagePlaceholder = () => {
  const companyName = useBrandingStore.getState().companyName || 'your healthcare provider';
  return `Hello, this is ${companyName} calling about your availability...`;
};

// Form validation schema
const configFormSchema = z
  .object({
    name: z.string().min(2, 'Configuration name must be at least 2 characters'),
    provider: z.enum(['gemini', 'openai']),
    call_provider: z.enum(['twilio', 'telnyx']),
    retry_limit: z.number().int().min(0, 'Minimum is 0').max(10, 'Maximum is 10'),
    retry_delay_minutes: z
      .number()
      .int()
      .min(1, 'Minimum is 1 minute')
      .max(240, 'Maximum is 240 minutes'),
    call_window_start: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format'),
    call_window_end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format'),
    inbound_twilio_phone_number_id: z.string().optional(),
    default_outbound_twilio_phone_number_id: z.string().optional(),
    inbound_telnyx_phone_number_id: z.string().optional(),
    default_outbound_telnyx_phone_number_id: z.string().optional(),
    workflow_id: z.string().optional(),
    messaging_workflow_id: z.string().optional(),
    session_start_workflow_id: z.string().optional(),
    prompt_rules: z.string().optional(),
    prompt_template: z.string().min(10, 'Prompt template must be at least 10 characters'),
    text_prompt_template: z.string().optional(),
    voice_name: z.string().optional(),
    llm_model: z.string().optional(),
    text_llm_model: z.string().optional(),
    welcome_message: z.string().optional(),
    missed_call_sms_message: z.string().optional(),
    followup_sms_message: z.string().optional(),
    advanced: z
      .string()
      .optional()
      .refine((value) => {
        try {
          parseAdvancedJson(value);
          return true;
        } catch {
          return false;
        }
      }, 'Advanced configuration must be valid JSON object'),
    tools: z.array(z.string()),
    custom_tool_ids: z.array(z.string()),
    max_call_duration_minutes: z.number().int().min(1).max(20),
    multi_agent_enabled: z.boolean(),
    agent_graph: z.custom<AgentGraph>().optional().nullable(),
    text_agent_graph: z.custom<AgentGraph>().optional().nullable(),
    notify_on_exhausted_attempts: z.boolean(),
    transfer_targets: z.array(transferTargetFormSchema),
    transfer_schedule: transferScheduleFormSchema,
    transfer_phone_number: z
      .string()
      .optional()
      .refine(
        (value) => !value?.trim() || E164_PHONE_REGEX.test(value.trim()),
        'Must be a valid E.164 phone number (e.g., +441234567890)',
      ),
    widget_enabled: z.boolean(),
    widget_text_chat_enabled: z.boolean(),
    widget_whatsapp_only: z.boolean(),
    widget_allowed_origins: z.array(z.string()),
    widget_rate_limit_per_hour: z.number().int().min(1).max(1000),
    widget_branding: z.object({
      title: z.string().optional(),
      subtitle: z.string().optional(),
      logo_url: z.string().optional(),
      logo_style: z.string().optional(),
      primary_color: z.string().optional(),
      welcome_message: z.string().optional(),
      tool_display_mode: z.enum(['inline', 'overlay']).optional(),
    }),
    widget_custom_css: z.string().optional(),
    whatsapp_number: z
      .string()
      .optional()
      .refine(
        (value) => !value?.trim() || E164_PHONE_REGEX.test(value.trim()),
        'Must be a valid E.164 phone number (e.g., +441234567890)',
      ),
    whatsapp_numbers: z.array(
      z.object({
        number: z
          .string()
          .trim()
          .regex(E164_PHONE_REGEX, 'Must be a valid E.164 phone number (e.g., +441234567890)'),
        label: z.string().trim().max(50).optional(),
      }),
    ),
    inbound_email_connector_id: z.string().optional(),
    inbound_email_address: z
      .string()
      .optional()
      .refine(
        (value) => !value?.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
        'Must be a valid email address',
      ),
    email_template: z.string().optional(),
    dashboard_api_key: z.string().optional(),
    // Widget display for the built-in Send Finance Link tool (stored on the
    // configuration as builtin_tool_displays.send_finance_link).
    finance_link_display: z.object({
      enabled: z.boolean(),
      type: z.enum(['carousel', 'cards', 'notice', 'badges', 'links']),
      array_path: z.string(),
      title: z.string(),
      link: z.string(),
      description: z.string(),
      fixed: z.boolean(),
    }),
  })
  .refine(
    (data) => {
      if (data.tools.includes('transfer_to_human')) {
        const hasTransferTargets = data.transfer_targets.some(
          (target) =>
            target.active &&
            target.category_key.trim() &&
            target.category_description.trim() &&
            E164_PHONE_REGEX.test(target.phone_number.trim()),
        );
        const legacyNumber = data.transfer_phone_number?.trim();
        return hasTransferTargets || (legacyNumber ? E164_PHONE_REGEX.test(legacyNumber) : false);
      }
      return true;
    },
    {
      message:
        'Add at least one active transfer category with a valid number or set a legacy fallback number.',
      path: ['transfer_targets'],
    },
  )
  .refine(
    (data) => {
      const activeKeys = data.transfer_targets
        .filter((target) => target.active)
        .map((target) => target.category_key.trim().toLowerCase())
        .filter(Boolean);
      return new Set(activeKeys).size === activeKeys.length;
    },
    {
      message: 'Transfer category keys must be unique.',
      path: ['transfer_targets'],
    },
  )
  .refine(
    (data) =>
      data.transfer_targets.filter((target) => target.active && target.is_default).length <= 1,
    {
      message: 'Only one active transfer category can be marked as default.',
      path: ['transfer_targets'],
    },
  );

type ConfigFormValues = z.infer<typeof configFormSchema>;

type TransferSchedulePreset = 'weekdays' | 'all' | 'clear' | 'always';

const ALL_DAY_START_TIME = '00:00';
const ALL_DAY_END_TIME = '23:59';
const DEFAULT_TRANSFER_DAY_START = '09:00';
const DEFAULT_TRANSFER_DAY_END = '17:00';

const isAllDayScheduleEntry = (entry: TransferScheduleEntry): boolean =>
  entry.start_time === ALL_DAY_START_TIME && entry.end_time === ALL_DAY_END_TIME;

type TransferScheduleEditorProps = {
  transferSchedule: TransferScheduleEntry[];
  register: UseFormRegister<ConfigFormValues>;
  errors: FieldErrors<ConfigFormValues>;
  onApplyPreset: (preset: TransferSchedulePreset) => void;
  onToggleAllDay: (index: number, checked: boolean) => void;
};

const TransferScheduleEditor = ({
  transferSchedule,
  register,
  errors,
  onApplyPreset,
  onToggleAllDay,
}: TransferScheduleEditorProps) => {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="flex items-center gap-1 text-xs font-semibold uppercase">
            <CalendarDays className="h-3.5 w-3.5" /> Human Transfer Schedule (UTC)
          </p>
          <p className="text-muted-foreground text-[11px]">
            Enable the days and hours when live transfer is allowed. If all days are disabled,
            transfers remain available at all times.
          </p>
        </div>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => onApplyPreset('weekdays')}
          >
            Weekdays
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => onApplyPreset('all')}
          >
            All Days
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={() => onApplyPreset('always')}
          >
            24/7
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px]"
            onClick={() => onApplyPreset('clear')}
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {transferSchedule.map((entry, index) => (
          <div
            key={`transfer-schedule-${entry.day}`}
            className="bg-background grid items-center gap-2 rounded-md border p-2 md:grid-cols-[140px_120px_1fr_1fr]"
          >
            <label className="flex items-center gap-2 text-xs font-medium">
              <input
                type="checkbox"
                className="border-input text-primary h-4 w-4 rounded"
                {...register(`transfer_schedule.${index}.enabled` as const)}
              />
              {TRANSFER_SCHEDULE_DAY_LABEL[entry.day]}
            </label>

            <label className="flex items-center gap-2 text-xs font-medium">
              <input
                type="checkbox"
                className="border-input text-primary h-4 w-4 rounded"
                checked={isAllDayScheduleEntry(entry)}
                onChange={(event) => onToggleAllDay(index, event.target.checked)}
                disabled={!entry.enabled}
              />
              All hours
            </label>

            <FormField
              label="Start"
              htmlFor={`transfer_schedule.${index}.start_time`}
              error={errors.transfer_schedule?.[index]?.start_time?.message}
            >
              <FormInput
                id={`transfer_schedule.${index}.start_time`}
                type="time"
                disabled={!entry.enabled || isAllDayScheduleEntry(entry)}
                error={!!errors.transfer_schedule?.[index]?.start_time}
                {...register(`transfer_schedule.${index}.start_time` as const)}
              />
            </FormField>

            <FormField
              label="End"
              htmlFor={`transfer_schedule.${index}.end_time`}
              error={errors.transfer_schedule?.[index]?.end_time?.message}
            >
              <FormInput
                id={`transfer_schedule.${index}.end_time`}
                type="time"
                disabled={!entry.enabled || isAllDayScheduleEntry(entry)}
                error={!!errors.transfer_schedule?.[index]?.end_time}
                {...register(`transfer_schedule.${index}.end_time` as const)}
              />
            </FormField>
          </div>
        ))}
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BrandingLivePreview = ({ control }: { control: any }) => {
  const branding = useWatch({ control, name: 'widget_branding' }) as
    | {
        title?: string;
        subtitle?: string;
        logo_url?: string;
        logo_style?: string;
        primary_color?: string;
        welcome_message?: string;
        tool_display_mode?: 'inline' | 'overlay';
      }
    | undefined;
  const name = useWatch({ control, name: 'name' }) as string | undefined;
  const welcomeTop = useWatch({ control, name: 'welcome_message' }) as string | undefined;
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs font-semibold uppercase">Live Preview</p>
      <WidgetPreview
        branding={{
          ...(branding ?? {}),
          welcome_message: branding?.welcome_message || welcomeTop,
        }}
        configName={name}
      />
    </div>
  );
};

// Built-in tool whose widget display is configurable from this form.
const FINANCE_LINK_TOOL = 'send_finance_link';

type FinanceLinkDisplayForm = ConfigFormValues['finance_link_display'];

// Map a saved builtin_tool_displays.send_finance_link config to the flat form
// shape, falling back to sensible defaults for the tool's `links` output.
const financeLinkDisplayDefault = (
  initialValues: ConfigurationFormProps['initialValues'],
): FinanceLinkDisplayForm => {
  const saved = initialValues?.builtin_tool_displays?.[FINANCE_LINK_TOOL];
  return {
    enabled: !!saved,
    type: (saved?.type as FinanceLinkDisplayForm['type']) ?? 'links',
    array_path: saved?.array_path ?? 'links',
    title: saved?.fields?.title ?? 'title',
    link: saved?.fields?.link ?? 'url',
    description: saved?.fields?.description ?? 'description',
    fixed: saved?.fixed ?? false,
  };
};

export const ConfigurationForm = ({
  initialValues,
  onSubmit,
  errors: apiErrors = [],
  isLoading = false,
  submitLabel = 'Save configuration',
}: ConfigurationFormProps) => {
  const [advancedEditorMode, setAdvancedEditorMode] = useState<'form' | 'json'>('form');
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [textPromptEditorOpen, setTextPromptEditorOpen] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const { data: twilioNumbers = [], isLoading: isLoadingTwilioNumbers } = useTwilioNumbers();
  const { data: telnyxNumbers = [] } = useTelnyxNumbers();
  const { data: emailConnectors = [] } = useEmailConnectors();
  // Whether the global master Dashboard API Key is set (Settings > API). When it is,
  // per-configuration keys are ignored, so the field below is locked.
  const { data: apiSettings = [] } = useSettings('api');
  const dashboardMasterKeySet = apiSettings.some(
    (s) => s.key === 'DASHBOARD_API_KEY' && s.has_value === true,
  );
  const { data: workflows = [], isLoading: workflowsLoading } = useWorkflows();
  // The saved workflow id can arrive before the workflow list. Keep it as an option so the
  // select never falls back to "None" while the list is still loading.
  const renderWorkflowOptions = (value: string | undefined, markDefault: boolean) => (
    <>
      {value && !workflows.some((wf) => wf.id === value) && (
        <option value={value}>
          {workflowsLoading ? 'Loading saved workflow…' : 'Saved workflow (not found)'}
        </option>
      )}
      {workflows.map((wf) => (
        <option key={wf.id} value={wf.id}>
          {wf.name}
          {markDefault && wf.is_default ? ' (Default)' : ''}
        </option>
      ))}
    </>
  );
  const { data: tools = [] } = useTools();
  const { data: customToolsData } = useCustomTools();
  const createCustomToolMutation = useCreateCustomTool();
  const updateCustomToolMutation = useUpdateCustomTool();
  const deleteCustomToolMutation = useDeleteCustomTool();

  const createVersionMutation = useCreateVersion();
  const setActiveVersionMutation = useSetActiveVersion();
  const deleteVersionMutation = useDeleteVersion();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    initialValues?.active_version_id || null,
  );

  const [pendingSelectionId, setPendingSelectionId] = useState<string | null>(null);

  const versions = useMemo(() => initialValues?.versions ?? [], [initialValues?.versions]);
  const resetSourceKey = initialValues
    ? [
        initialValues.id,
        String(initialValues.updated_at),
        String(initialValues.active_version_id ?? ''),
        String(initialValues.active_version?.updated_at ?? ''),
        String(initialValues.versions?.length ?? 0),
      ].join('|')
    : 'new-config';
  const lastAppliedResetSourceRef = useRef<string | null>(null);
  const lastHydratedVersionRef = useRef<string | null>(null);

  useEffect(() => {
    if (pendingSelectionId && versions.some((v) => v.id === pendingSelectionId)) {
      setSelectedVersionId(pendingSelectionId);
      setPendingSelectionId(null);
    }
  }, [versions, pendingSelectionId]);

  const [deleteVersionTarget, setDeleteVersionTarget] = useState<{
    id: string;
    number: number;
    callCount: number;
  } | null>(null);

  const [customToolModalState, setCustomToolModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    toolId: string | null;
    initialValues: CustomToolDetail | null;
  }>({
    open: false,
    mode: 'create',
    toolId: null,
    initialValues: null,
  });

  const [customToolModalErrors, setCustomToolModalErrors] = useState<
    Array<{ field: string; message: string }>
  >([]);
  const [isSavingCustomTool, setIsSavingCustomTool] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    toolId: string | null;
    toolName: string;
    configurations: Array<{ id: string; name: string }>;
  }>({
    open: false,
    toolId: null,
    toolName: '',
    configurations: [],
  });
  const { toast } = useToast();

  const activeVersion = initialValues?.active_version;

  const selectedVersion = versions.find((v) => v.id === selectedVersionId) || activeVersion;
  const selectedVersionHydrationKey = selectedVersion
    ? [
        selectedVersion.id,
        String(selectedVersion.version_number),
        String(selectedVersion.updated_at),
      ].join('|')
    : 'no-version';
  const defaultValues: ConfigFormValues = {
    name: initialValues?.name ?? '',
    provider: (initialValues?.provider as 'gemini' | 'openai') ?? DEFAULT_PROVIDER,
    call_provider:
      (initialValues as { call_provider?: 'twilio' | 'telnyx' } | undefined)?.call_provider ??
      'twilio',
    retry_limit: initialValues?.retry_limit ?? 3,
    retry_delay_minutes: initialValues?.retry_delay_minutes ?? 30,
    call_window_start: initialValues?.call_window_start
      ? convertUTCToLocalTime(initialValues.call_window_start)
      : '08:00',
    call_window_end: initialValues?.call_window_end
      ? convertUTCToLocalTime(initialValues.call_window_end)
      : '18:00',
    inbound_twilio_phone_number_id: initialValues?.inbound_twilio_phone_number_id ?? '',
    default_outbound_twilio_phone_number_id:
      initialValues?.default_outbound_twilio_phone_number_id ?? '',
    inbound_telnyx_phone_number_id:
      (initialValues as { inbound_telnyx_phone_number_id?: string } | undefined)
        ?.inbound_telnyx_phone_number_id ?? '',
    default_outbound_telnyx_phone_number_id:
      (initialValues as { default_outbound_telnyx_phone_number_id?: string } | undefined)
        ?.default_outbound_telnyx_phone_number_id ?? '',
    workflow_id: initialValues?.workflow_id ?? '',
    messaging_workflow_id:
      ((initialValues as Record<string, unknown> | undefined)?.messaging_workflow_id as string) ??
      '',
    session_start_workflow_id:
      ((initialValues as Record<string, unknown> | undefined)
        ?.session_start_workflow_id as string) ?? '',
    prompt_rules:
      ((initialValues as Record<string, unknown> | undefined)?.prompt_rules as string) ?? '',
    prompt_template: selectedVersion?.prompt_template ?? '',
    text_prompt_template: selectedVersion?.text_prompt_template ?? '',
    voice_name:
      initialValues?.voice_name ??
      ((initialValues?.provider as any) === 'openai' ? DEFAULT_OPENAI_VOICE : DEFAULT_GEMINI_VOICE),
    llm_model:
      initialValues?.llm_model ??
      ((initialValues?.provider as any) === 'openai' ? DEFAULT_OPENAI_MODEL : DEFAULT_GEMINI_MODEL),
    text_llm_model: (initialValues as Record<string, unknown> | undefined)?.text_llm_model as
      | string
      | undefined,
    advanced: formatAdvancedJson(
      (initialValues?.advanced as Record<string, unknown> | undefined) ?? {},
      initialValues?.voice_name,
      initialValues?.llm_model,
    ),
    welcome_message: selectedVersion?.welcome_message ?? '',
    missed_call_sms_message: selectedVersion?.missed_call_sms_message ?? '',
    followup_sms_message: selectedVersion?.followup_sms_message ?? '',
    tools: initialValues?.tools ?? ['capture_availability', 'capture_consent'],
    custom_tool_ids: initialValues?.custom_tool_ids ?? [],
    max_call_duration_minutes: initialValues?.max_call_duration_minutes ?? 10,
    multi_agent_enabled: initialValues?.multi_agent_enabled ?? false,
    agent_graph: (initialValues as Record<string, unknown> | undefined)?.agent_graph as
      | AgentGraph
      | null
      | undefined,
    text_agent_graph: (initialValues as Record<string, unknown> | undefined)?.text_agent_graph as
      | AgentGraph
      | null
      | undefined,
    notify_on_exhausted_attempts: initialValues?.notify_on_exhausted_attempts ?? false,
    transfer_targets: initialValues?.transfer_targets ?? [],
    transfer_schedule: normalizeTransferScheduleForForm(initialValues?.transfer_schedule),
    transfer_phone_number: initialValues?.transfer_phone_number ?? '',
    widget_enabled: initialValues?.widget_enabled ?? false,
    widget_text_chat_enabled: initialValues?.widget_text_chat_enabled ?? true,
    widget_whatsapp_only: initialValues?.widget_whatsapp_only ?? false,
    widget_allowed_origins: initialValues?.widget_allowed_origins ?? [],
    widget_rate_limit_per_hour: initialValues?.widget_rate_limit_per_hour ?? 10,
    widget_branding: initialValues?.widget_branding ?? {},
    widget_custom_css:
      ((initialValues as Record<string, unknown> | undefined)?.widget_custom_css as string) ?? '',
    whatsapp_number: initialValues?.whatsapp_number ?? '',
    whatsapp_numbers: seedWhatsappNumbers(
      initialValues?.whatsapp_numbers,
      initialValues?.whatsapp_number,
    ),
    inbound_email_connector_id: initialValues?.inbound_email_connector_id ?? '',
    inbound_email_address: initialValues?.inbound_email_address ?? '',
    email_template: initialValues?.email_template ?? DEFAULT_EMAIL_TEMPLATE,
    dashboard_api_key: initialValues?.dashboard_api_key ?? '',
    finance_link_display: financeLinkDisplayDefault(initialValues),
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    reset,
    control,
    formState: { errors, dirtyFields },
  } = useForm<ConfigFormValues>({
    resolver: zodResolver(configFormSchema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    fields: transferTargetFields,
    append: appendTransferTarget,
    remove: removeTransferTarget,
  } = useFieldArray({
    control,
    name: 'transfer_targets',
  });

  const {
    fields: whatsappNumberFields,
    append: appendWhatsappNumber,
    remove: removeWhatsappNumber,
  } = useFieldArray({
    control,
    name: 'whatsapp_numbers',
  });

  // Keep advanced registered even when the accordion is closed or JSON editor is hidden.
  useEffect(() => {
    register('advanced');
  }, [register]);

  // Keep selectedVersionId and form values in sync with incoming initialValues.
  useEffect(() => {
    if (lastAppliedResetSourceRef.current === resetSourceKey) {
      return;
    }
    lastAppliedResetSourceRef.current = resetSourceKey;

    // If there are no initial values (e.g. creating a new configuration),
    // reset to the "empty" defaults and clear the selected version.
    if (!initialValues) {
      setSelectedVersionId(null);
      reset({
        name: '',
        provider: DEFAULT_PROVIDER as 'gemini' | 'openai',
        call_provider: 'twilio',
        retry_limit: 3,
        retry_delay_minutes: 30,
        call_window_start: '08:00',
        call_window_end: '18:00',
        inbound_twilio_phone_number_id: '',
        default_outbound_twilio_phone_number_id: '',
        inbound_telnyx_phone_number_id: '',
        default_outbound_telnyx_phone_number_id: '',
        workflow_id: '',
        messaging_workflow_id: '',
        session_start_workflow_id: '',
        prompt_rules: '',
        prompt_template: '',
        text_prompt_template: '',
        voice_name:
          (DEFAULT_PROVIDER as string) === 'openai' ? DEFAULT_OPENAI_VOICE : DEFAULT_GEMINI_VOICE,
        llm_model:
          (DEFAULT_PROVIDER as string) === 'openai' ? DEFAULT_OPENAI_MODEL : DEFAULT_GEMINI_MODEL,
        text_llm_model: undefined,
        advanced: '{}',
        welcome_message: '',
        missed_call_sms_message: '',
        followup_sms_message: '',
        tools: ['capture_availability', 'capture_consent'],
        custom_tool_ids: [],
        max_call_duration_minutes: 5,
        multi_agent_enabled: false,
        agent_graph: null,
        text_agent_graph: null,
        notify_on_exhausted_attempts: false,
        transfer_targets: [],
        transfer_schedule: cloneDefaultTransferSchedule(),
        transfer_phone_number: '',
        widget_enabled: false,
        widget_text_chat_enabled: true,
        widget_whatsapp_only: false,
        widget_allowed_origins: [],
        widget_rate_limit_per_hour: 10,
        widget_branding: {},
        widget_custom_css: '',
        whatsapp_number: '',
        whatsapp_numbers: [],
        inbound_email_connector_id: '',
        inbound_email_address: '',
        email_template: DEFAULT_EMAIL_TEMPLATE,
        dashboard_api_key: '',
        finance_link_display: financeLinkDisplayDefault(null),
      });
      return;
    }

    const nextSelectedVersionId = initialValues.active_version_id ?? null;
    const nextSelectedVersion =
      initialValues.versions?.find((v: ConfigurationVersion) => v.id === nextSelectedVersionId) ??
      initialValues.active_version;

    setSelectedVersionId(nextSelectedVersionId);

    reset({
      name: initialValues.name ?? '',
      provider: (initialValues.provider as string) === 'openai' ? 'openai' : 'gemini',
      call_provider:
        (initialValues as { call_provider?: string }).call_provider === 'telnyx'
          ? 'telnyx'
          : 'twilio',
      retry_limit: initialValues.retry_limit ?? 3,
      retry_delay_minutes: initialValues.retry_delay_minutes ?? 30,
      call_window_start: initialValues.call_window_start
        ? convertUTCToLocalTime(initialValues.call_window_start)
        : '08:00',
      call_window_end: initialValues.call_window_end
        ? convertUTCToLocalTime(initialValues.call_window_end)
        : '18:00',
      inbound_twilio_phone_number_id: initialValues.inbound_twilio_phone_number_id ?? '',
      default_outbound_twilio_phone_number_id:
        initialValues.default_outbound_twilio_phone_number_id ?? '',
      inbound_telnyx_phone_number_id:
        (initialValues as { inbound_telnyx_phone_number_id?: string })
          .inbound_telnyx_phone_number_id ?? '',
      default_outbound_telnyx_phone_number_id:
        (initialValues as { default_outbound_telnyx_phone_number_id?: string })
          .default_outbound_telnyx_phone_number_id ?? '',
      workflow_id: initialValues.workflow_id ?? '',
      messaging_workflow_id:
        ((initialValues as Record<string, unknown>).messaging_workflow_id as string) ?? '',
      session_start_workflow_id:
        ((initialValues as Record<string, unknown>).session_start_workflow_id as string) ?? '',
      prompt_rules: ((initialValues as Record<string, unknown>).prompt_rules as string) ?? '',
      prompt_template: nextSelectedVersion?.prompt_template ?? '',
      text_prompt_template: nextSelectedVersion?.text_prompt_template ?? '',
      voice_name:
        initialValues.voice_name ??
        (initialValues.provider === 'openai' ? DEFAULT_OPENAI_VOICE : DEFAULT_GEMINI_VOICE),
      llm_model:
        initialValues.llm_model ??
        (initialValues.provider === 'openai' ? DEFAULT_OPENAI_MODEL : DEFAULT_GEMINI_MODEL),
      text_llm_model:
        ((initialValues as Record<string, unknown>).text_llm_model as string) ?? undefined,
      advanced: formatAdvancedJson(
        (initialValues.advanced as Record<string, unknown> | undefined) ?? {},
        initialValues.voice_name,
        initialValues.llm_model,
      ),
      welcome_message: nextSelectedVersion?.welcome_message ?? '',
      missed_call_sms_message: nextSelectedVersion?.missed_call_sms_message ?? '',
      followup_sms_message: nextSelectedVersion?.followup_sms_message ?? '',
      tools: initialValues.tools ?? ['capture_availability', 'capture_consent'],
      custom_tool_ids: initialValues.custom_tool_ids ?? [],
      max_call_duration_minutes: initialValues.max_call_duration_minutes ?? 10,
      multi_agent_enabled: initialValues.multi_agent_enabled ?? false,
      agent_graph: (initialValues as Record<string, unknown>).agent_graph as
        | AgentGraph
        | null
        | undefined,
      text_agent_graph: (initialValues as Record<string, unknown>).text_agent_graph as
        | AgentGraph
        | null
        | undefined,
      notify_on_exhausted_attempts: initialValues.notify_on_exhausted_attempts ?? false,
      transfer_targets: initialValues.transfer_targets ?? [],
      transfer_schedule: normalizeTransferScheduleForForm(initialValues.transfer_schedule),
      transfer_phone_number: initialValues.transfer_phone_number ?? '',
      widget_enabled: initialValues.widget_enabled ?? false,
      widget_text_chat_enabled: initialValues.widget_text_chat_enabled ?? true,
      widget_whatsapp_only: initialValues.widget_whatsapp_only ?? false,
      widget_allowed_origins: initialValues.widget_allowed_origins ?? [],
      widget_rate_limit_per_hour: initialValues.widget_rate_limit_per_hour ?? 10,
      widget_branding: initialValues.widget_branding ?? {},
      widget_custom_css:
        ((initialValues as Record<string, unknown>).widget_custom_css as string) ?? '',
      whatsapp_number: initialValues.whatsapp_number ?? '',
      whatsapp_numbers: seedWhatsappNumbers(
        initialValues.whatsapp_numbers,
        initialValues.whatsapp_number,
      ),
      inbound_email_connector_id: initialValues.inbound_email_connector_id ?? '',
      inbound_email_address: initialValues.inbound_email_address ?? '',
      email_template: initialValues.email_template ?? DEFAULT_EMAIL_TEMPLATE,
      dashboard_api_key: initialValues.dashboard_api_key ?? '',
      finance_link_display: financeLinkDisplayDefault(initialValues),
    });
  }, [initialValues, reset, resetSourceKey]);

  const selectedProvider = useWatch({ control, name: 'provider' });
  const selectedCallProvider = useWatch({ control, name: 'call_provider' });
  const selectedModel = useWatch({ control, name: 'llm_model' });
  const selectedTools = useWatch({ control, name: 'tools' });
  const selectedCustomToolIds = useWatch({ control, name: 'custom_tool_ids' });
  const financeDisplayEnabled = useWatch({ control, name: 'finance_link_display.enabled' });
  const financeDisplayType = useWatch({ control, name: 'finance_link_display.type' });
  const multiAgentEnabled = useWatch({ control, name: 'multi_agent_enabled' });
  const agentGraph = (useWatch({ control, name: 'agent_graph' }) as AgentGraph | null) ?? null;
  const textAgentGraph =
    (useWatch({ control, name: 'text_agent_graph' }) as AgentGraph | null) ?? null;
  const transferTargets = useWatch({ control, name: 'transfer_targets' }) ?? [];
  const transferSchedule = useWatch({ control, name: 'transfer_schedule' }) ?? [];
  const advancedRaw = useWatch({ control, name: 'advanced' });
  const widgetEnabled = useWatch({ control, name: 'widget_enabled' });
  const whatsappOnly = useWatch({ control, name: 'widget_whatsapp_only' });
  const whatsappNumbers = useWatch({ control, name: 'whatsapp_numbers' }) ?? [];
  const promptTemplate = useWatch({ control, name: 'prompt_template' });
  const textPromptTemplate = useWatch({ control, name: 'text_prompt_template' });
  const watchedConnectorId = useWatch({ control, name: 'inbound_email_connector_id' });
  const advancedObject = parseAdvancedJsonSafe(advancedRaw);
  const isGeminiNativeAudioSelected =
    selectedProvider === 'gemini' && isGeminiNativeAudioModel(selectedModel ?? undefined);
  const isGeminiThinkingSelected =
    selectedProvider === 'gemini' && isGeminiThinkingLiveModel(selectedModel ?? undefined);
  const isGemini35Selected =
    selectedProvider === 'gemini' && isGemini35LiveModel(selectedModel ?? undefined);
  const isOpenAISelected = selectedProvider === 'openai';
  const isGemini38Selected =
    selectedProvider === 'gemini' && isGemini38Model(selectedModel ?? undefined);
  const isGemini38ThinkingSelected = isGemini38Selected && selectedModel === GEMINI_38_THINKING;
  const thinkingLevelOptions = isGemini38ThinkingSelected
    ? GEMINI_THINKING_LEVEL_OPTIONS.filter((level) => level.value !== 'minimal')
    : GEMINI_THINKING_LEVEL_OPTIONS;
  const hasValidThinkingLevel =
    typeof advancedObject.thinking_level === 'string' &&
    thinkingLevelOptions.some((level) => level.value === advancedObject.thinking_level);
  const selectedThinkingLevel = hasValidThinkingLevel
    ? String(advancedObject.thinking_level)
    : isGemini38ThinkingSelected
      ? 'low'
      : mapThinkingBudgetToLevel(advancedObject.thinking_budget);

  const customTools = customToolsData?.data ?? [];

  // Session-wide tool names, including tools owned by non-entry voice agents.
  const executionToolOptions = (() => {
    const agents = multiAgentEnabled
      ? (agentGraph?.agents ?? []).filter((agent) => agent.type === 'ai_agent')
      : [];
    const builtinNames = new Set([
      ...selectedTools,
      ...agents.flatMap((agent) => agent.tools ?? []),
    ]);
    const customIds = new Set([
      ...selectedCustomToolIds,
      ...agents.flatMap((agent) => agent.custom_tool_ids ?? []),
    ]);
    const options = new Map<string, string>();
    for (const name of builtinNames)
      options.set(name, tools.find((tool) => tool.value === name)?.label ?? name);
    for (const tool of customTools) if (customIds.has(tool.id)) options.set(tool.name, tool.name);
    if (multiAgentEnabled) {
      options.set('transfer', 'Agent transfer (direct graph)');
      options.set('transfer_to_agent', 'Agent transfer (ADK / legacy)');
    }
    return [...options].map(([name, label]) => ({ name, label }));
  })();

  const knownToolValues = useMemo(() => new Set<string>(tools.map((t) => t.value)), [tools]);
  const knownCustomToolIds = useMemo(() => new Set(customTools.map((t) => t.id)), [customTools]);

  const orphanedBuiltinTools = useMemo(
    () => selectedTools.filter((v) => !knownToolValues.has(v)),
    [selectedTools, knownToolValues],
  );
  const orphanedCustomToolIds = useMemo(
    () => selectedCustomToolIds.filter((id) => !knownCustomToolIds.has(id)),
    [selectedCustomToolIds, knownCustomToolIds],
  );

  // Update text fields when selected version changes
  useEffect(() => {
    if (lastHydratedVersionRef.current === selectedVersionHydrationKey) {
      return;
    }
    lastHydratedVersionRef.current = selectedVersionHydrationKey;

    if (selectedVersion) {
      setValue('prompt_template', selectedVersion.prompt_template);
      setValue('text_prompt_template', selectedVersion.text_prompt_template || '');
      setValue('welcome_message', selectedVersion.welcome_message || '');
      setValue('missed_call_sms_message', selectedVersion.missed_call_sms_message || '');
      setValue('followup_sms_message', selectedVersion.followup_sms_message || '');
    }
  }, [selectedVersion, selectedVersionHydrationKey, setValue]);

  // Merge API errors when they change
  useEffect(() => {
    if (apiErrors.length > 0) {
      mergeApiErrors(apiErrors, setError);
    }
  }, [apiErrors, setError]);

  const voiceOptions = selectedProvider === 'openai' ? OPENAI_VOICE_OPTIONS : GEMINI_VOICE_OPTIONS;
  const modelOptions = selectedProvider === 'openai' ? OPENAI_MODEL_OPTIONS : GEMINI_MODEL_OPTIONS;

  const onFormSubmit = handleSubmit(
    (data) => {
      const advanced =
        data.advanced === undefined
          ? ((initialValues?.advanced as Record<string, unknown> | undefined) ?? {})
          : parseAdvancedJson(data.advanced);
      const formModel = data.llm_model || undefined;
      const formVoice = data.voice_name || undefined;

      // Keep advanced model/voice in sync with the primary selectors to avoid stale overrides.
      const advancedForSubmit: Record<string, unknown> = {
        ...advanced,
        ...(formModel ? { model: formModel } : {}),
        ...(formVoice ? { voice_name: formVoice } : {}),
      };

      const effectiveModel =
        (typeof advancedForSubmit.model === 'string' ? advancedForSubmit.model : undefined) ||
        formModel;
      const normalizedAdvanced = normalizeAdvancedForModel(
        advancedForSubmit,
        data.provider,
        effectiveModel,
      );
      const advancedVoiceName =
        typeof normalizedAdvanced.voice_name === 'string'
          ? normalizedAdvanced.voice_name
          : formVoice;
      const advancedModel =
        typeof normalizedAdvanced.model === 'string' ? normalizedAdvanced.model : formModel;

      // Built-in tool widget displays, keyed by tool name. Only the Send Finance
      // Link tool is editable here today; an empty map clears any saved display.
      const fld = data.finance_link_display;
      const builtinToolDisplays: NonNullable<UpdateConfigurationSetupDto['builtin_tool_displays']> =
        fld.enabled
          ? {
              [FINANCE_LINK_TOOL]: {
                type: fld.type,
                ...(fld.type !== 'notice' && { array_path: fld.array_path.trim() || 'links' }),
                fields: {
                  title: fld.title.trim() || 'title',
                  ...(fld.link.trim() && { link: fld.link.trim() }),
                  ...(fld.description.trim() && { description: fld.description.trim() }),
                },
                ...(fld.fixed && { fixed: true }),
              },
            }
          : {};

      const dto: UpdateConfigurationSetupDto = {
        name: data.name,
        provider: data.provider,
        call_provider: data.call_provider,
        retry_limit: data.retry_limit,
        retry_delay_minutes: data.retry_delay_minutes,
        call_window_start: convertLocalTimeToUTC(data.call_window_start),
        call_window_end: convertLocalTimeToUTC(data.call_window_end),
        // Only include number assignments if the user explicitly touched them.
        // Prevents wiping an existing assignment when the select wasn't yet
        // populated with options on first render.
        ...(dirtyFields.inbound_twilio_phone_number_id
          ? {
              inbound_twilio_phone_number_id: data.inbound_twilio_phone_number_id?.trim()
                ? data.inbound_twilio_phone_number_id
                : null,
            }
          : {}),
        ...(dirtyFields.default_outbound_twilio_phone_number_id
          ? {
              default_outbound_twilio_phone_number_id:
                data.default_outbound_twilio_phone_number_id?.trim()
                  ? data.default_outbound_twilio_phone_number_id
                  : null,
            }
          : {}),
        ...(dirtyFields.inbound_telnyx_phone_number_id
          ? {
              inbound_telnyx_phone_number_id: data.inbound_telnyx_phone_number_id?.trim()
                ? data.inbound_telnyx_phone_number_id
                : null,
            }
          : {}),
        ...(dirtyFields.default_outbound_telnyx_phone_number_id
          ? {
              default_outbound_telnyx_phone_number_id:
                data.default_outbound_telnyx_phone_number_id?.trim()
                  ? data.default_outbound_telnyx_phone_number_id
                  : null,
            }
          : {}),
        ...(dirtyFields.workflow_id
          ? {
              workflow_id: data.workflow_id?.trim() ? data.workflow_id : null,
            }
          : {}),
        ...(dirtyFields.messaging_workflow_id
          ? {
              messaging_workflow_id: data.messaging_workflow_id?.trim()
                ? data.messaging_workflow_id
                : null,
            }
          : {}),
        ...(dirtyFields.session_start_workflow_id
          ? {
              session_start_workflow_id: data.session_start_workflow_id?.trim()
                ? data.session_start_workflow_id
                : null,
            }
          : {}),
        prompt_rules: data.prompt_rules?.trim() || undefined,
        prompt_template: data.prompt_template,
        text_prompt_template: data.text_prompt_template?.trim() || undefined,
        voice_name: data.voice_name || undefined,
        llm_model: data.llm_model || undefined,
        text_llm_model: data.text_llm_model?.trim() || undefined,
        advanced: {
          ...normalizedAdvanced,
          voice_name: advancedVoiceName,
          model: advancedModel,
        },
        welcome_message: data.welcome_message || undefined,
        missed_call_sms_message: data.missed_call_sms_message?.trim(),
        followup_sms_message: data.followup_sms_message?.trim(),
        tools: data.tools as Tool[],
        custom_tool_ids: data.custom_tool_ids,
        max_call_duration_minutes: data.max_call_duration_minutes,
        multi_agent_enabled: data.multi_agent_enabled,
        agent_graph: (data as Record<string, unknown>).agent_graph as AgentGraph | null | undefined,
        text_agent_graph: (data as Record<string, unknown>).text_agent_graph as
          | AgentGraph
          | null
          | undefined,
        notify_on_exhausted_attempts: data.notify_on_exhausted_attempts,
        transfer_targets: data.transfer_targets
          .map((target: TransferTarget) => ({
            category_key: target.category_key.trim().toLowerCase(),
            category_description: target.category_description.trim(),
            phone_number: target.phone_number.trim(),
            sms_enabled: target.sms_enabled,
            is_default: target.is_default,
            notify_emails: target.notify_emails ?? [],
            active: target.active,
          }))
          .filter(
            (target: TransferTarget) =>
              target.category_key && target.category_description && target.phone_number,
          ),
        transfer_schedule: data.transfer_schedule
          .map((entry: TransferScheduleEntry) => ({
            day: entry.day,
            enabled: entry.enabled,
            start_time: entry.start_time,
            end_time: entry.end_time,
          }))
          .filter((entry: TransferScheduleEntry) => entry.enabled),
        transfer_phone_number: data.transfer_phone_number?.trim() || undefined,
        editing_version_id: selectedVersionId || undefined,
        widget_enabled: data.widget_enabled,
        widget_text_chat_enabled: data.widget_text_chat_enabled,
        widget_whatsapp_only: data.widget_whatsapp_only,
        widget_allowed_origins: data.widget_allowed_origins,
        widget_rate_limit_per_hour: data.widget_rate_limit_per_hour,
        widget_branding: data.widget_branding,
        widget_custom_css: data.widget_custom_css?.trim() || undefined,
        // whatsapp_number stays in sync with the first list entry, purely for
        // older backend consumers that only ever read the single field.
        // whatsapp_numbers is the real source of truth for the widget.
        whatsapp_number: data.whatsapp_numbers[0]?.number.trim() || undefined,
        whatsapp_numbers: data.whatsapp_numbers
          .map((entry) => ({
            number: entry.number.trim(),
            label: entry.label?.trim() || '',
          }))
          .filter((entry) => entry.number),
        inbound_email_connector_id: data.inbound_email_connector_id || undefined,
        inbound_email_address: data.inbound_email_address?.trim() || undefined,
        email_template: data.email_template?.trim() || undefined,
        dashboard_api_key: dashboardMasterKeySet
          ? undefined
          : data.dashboard_api_key?.trim() || null,
        builtin_tool_displays: builtinToolDisplays,
      };
      onSubmit?.(dto);
    },
    (formErrors) => {
      scrollToFirstError(formErrors);
    },
  );

  const handleCreateVersion = async () => {
    if (!initialValues?.id) return;
    try {
      const currentValues = getValues();
      const newVersion = await createVersionMutation.mutateAsync({
        configId: initialValues.id,
        data: {
          prompt_template: currentValues.prompt_template,
          text_prompt_template: currentValues.text_prompt_template || undefined,
          welcome_message: currentValues.welcome_message,
          missed_call_sms_message: currentValues.missed_call_sms_message,
          followup_sms_message: currentValues.followup_sms_message,
        },
      });
      setPendingSelectionId(newVersion.id);
      toast({ title: 'Success', description: `Version ${newVersion.version_number} created.` });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: extractErrorMessage(err) });
    }
  };

  const handleSetDefault = async () => {
    if (!initialValues?.id || !selectedVersionId) return;
    try {
      await setActiveVersionMutation.mutateAsync({
        configId: initialValues.id,
        versionId: selectedVersionId,
      });
      toast({ title: 'Success', description: 'Active version updated.' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: extractErrorMessage(err) });
    }
  };

  const handleDeleteVersion = async (version: ConfigurationVersion) => {
    if (!initialValues?.id) return;
    try {
      const callCount = await configurationsApi.getVersionCallCount(initialValues.id, version.id);
      setDeleteVersionTarget({ id: version.id, number: version.version_number, callCount });
    } catch (_err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch version data.',
      });
    }
  };

  const confirmDeleteVersion = async () => {
    if (!initialValues?.id || !deleteVersionTarget) return;
    try {
      await deleteVersionMutation.mutateAsync({
        configId: initialValues.id,
        versionId: deleteVersionTarget.id,
      });
      if (selectedVersionId === deleteVersionTarget.id) {
        setSelectedVersionId(initialValues?.active_version_id || null);
      }
      setDeleteVersionTarget(null);
      toast({ title: 'Success', description: 'Version deleted.' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: extractErrorMessage(err) });
    }
  };

  const handleToolToggle = (toolValue: string, checked: boolean) => {
    if (checked) {
      setValue('tools', [...selectedTools, toolValue]);
    } else {
      setValue(
        'tools',
        selectedTools.filter((t) => t !== toolValue),
      );
    }
  };

  const handleCustomToolToggle = (toolId: string, checked: boolean) => {
    if (checked) {
      setValue('custom_tool_ids', [...selectedCustomToolIds, toolId]);
    } else {
      setValue(
        'custom_tool_ids',
        selectedCustomToolIds.filter((id) => id !== toolId),
      );
    }
  };

  const handleAddTransferTarget = () => {
    appendTransferTarget(createEmptyTransferTarget());
  };

  const handleRemoveTransferTarget = (index: number) => {
    removeTransferTarget(index);
  };

  const handleAddWhatsappNumber = () => {
    appendWhatsappNumber(createEmptyWhatsappNumber());
  };

  const handleRemoveWhatsappNumber = (index: number) => {
    removeWhatsappNumber(index);
  };

  const handleDefaultTransferTargetToggle = (index: number, checked: boolean) => {
    const updatedTargets = transferTargets.map((target, targetIndex) => ({
      ...target,
      is_default: checked
        ? targetIndex === index
        : targetIndex === index
          ? false
          : target.is_default,
    }));

    setValue('transfer_targets', updatedTargets, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleApplyTransferSchedulePreset = (preset: TransferSchedulePreset) => {
    const baseSchedule =
      transferSchedule.length === TRANSFER_SCHEDULE_DAYS.length
        ? transferSchedule
        : cloneDefaultTransferSchedule();

    const updatedSchedule = baseSchedule.map((entry, index) => {
      if (preset === 'always') {
        return {
          ...entry,
          enabled: true,
          start_time: ALL_DAY_START_TIME,
          end_time: ALL_DAY_END_TIME,
        };
      }
      if (preset === 'all') {
        return { ...entry, enabled: true };
      }
      if (preset === 'clear') {
        return { ...entry, enabled: false };
      }
      return { ...entry, enabled: index < 5 };
    });

    setValue('transfer_schedule', updatedSchedule, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleToggleTransferScheduleAllDay = (index: number, checked: boolean) => {
    const baseSchedule =
      transferSchedule.length === TRANSFER_SCHEDULE_DAYS.length
        ? transferSchedule
        : cloneDefaultTransferSchedule();

    const updatedSchedule = baseSchedule.map((entry, currentIndex) => {
      if (currentIndex !== index) {
        return entry;
      }

      if (checked) {
        return {
          ...entry,
          enabled: true,
          start_time: ALL_DAY_START_TIME,
          end_time: ALL_DAY_END_TIME,
        };
      }

      if (isAllDayScheduleEntry(entry)) {
        return {
          ...entry,
          start_time: DEFAULT_TRANSFER_DAY_START,
          end_time: DEFAULT_TRANSFER_DAY_END,
        };
      }

      return entry;
    });

    setValue('transfer_schedule', updatedSchedule, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleAddTool = () => {
    setCustomToolModalErrors([]);
    setCustomToolModalState({
      open: true,
      mode: 'create',
      toolId: null,
      initialValues: null,
    });
  };

  const handleEditTool = (tool: CustomToolDetail) => {
    setCustomToolModalErrors([]);
    setCustomToolModalState({
      open: true,
      mode: 'edit',
      toolId: tool.id,
      initialValues: tool,
    });
  };

  const handleSubmitCustomTool = async (values: any) => {
    try {
      setCustomToolModalErrors([]);
      setIsSavingCustomTool(true);

      if (customToolModalState.mode === 'edit' && customToolModalState.toolId) {
        await updateCustomToolMutation.mutateAsync({
          id: customToolModalState.toolId,
          data: values,
        });
        toast({ title: 'Success', description: 'Tool updated.' });
      } else {
        await createCustomToolMutation.mutateAsync(values);
        toast({ title: 'Success', description: 'Tool created.' });
      }

      setCustomToolModalState((prev) => ({ ...prev, open: false }));
    } catch (error) {
      const validationErrors = extractValidationErrors(error);
      if (validationErrors.length > 0) setCustomToolModalErrors(validationErrors);
      else
        toast({ variant: 'destructive', title: 'Error', description: extractErrorMessage(error) });
    } finally {
      setIsSavingCustomTool(false);
    }
  };

  const handleDeleteTool = async (toolId: string, toolName: string) => {
    try {
      const usage = await toolsApi.getCustomToolUsage(toolId);
      setDeleteDialogState({ open: true, toolId, toolName, configurations: usage.configurations });
    } catch (_error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to check tool usage.' });
    }
  };

  const handleConfirmDeleteTool = async () => {
    if (!deleteDialogState.toolId) return;
    try {
      const force = deleteDialogState.configurations.length > 0;
      await deleteCustomToolMutation.mutateAsync({ id: deleteDialogState.toolId, force });
      setDeleteDialogState((prev) => ({ ...prev, open: false }));
      toast({ title: 'Success', description: 'Custom tool deleted.' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: extractErrorMessage(error) });
    }
  };

  const setAdvancedField = (
    key: string,
    value: string | number | boolean | Record<string, unknown> | undefined,
  ) => {
    const current = parseAdvancedJsonSafe(getValues('advanced'));

    if (value === '' || value === undefined) {
      delete current[key];
    } else {
      current[key] = value;
    }

    setValue('advanced', JSON.stringify(current, null, 2), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleExportConfig = () => {
    const values = getValues();
    const exportData = {
      ...values,
      _exportedAt: new Date().toISOString(),
      _version: '1.0',
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `config-${values.name || 'untitled'}-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: 'Configuration downloaded as JSON.' });
  };

  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string);
        const { _exportedAt, _version, ...formValues } = imported;
        reset({ ...defaultValues, ...formValues }, { keepDefaultValues: false });
        toast({ title: 'Imported', description: 'Configuration loaded from file.' });
      } catch {
        toast({ variant: 'destructive', title: 'Error', description: 'Invalid JSON file.' });
      }
    };
    reader.readAsText(file);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  const promptVariableGroups = useMemo(
    () => [
      {
        namespace: 'contact',
        variables: [
          { path: 'contact.name', label: 'Contact first name' },
          { path: 'contact.phone', label: 'Contact phone number' },
          { path: 'contact.data.email', label: 'Contact email' },
          { path: 'contact.data.vehicles_of_interest.0.make', label: 'Vehicle make' },
          { path: 'contact.data.vehicles_of_interest.0.model', label: 'Vehicle model' },
        ],
      },
      {
        namespace: 'time',
        variables: [
          { path: 'today', label: 'Current date' },
          { path: 'today:1', label: 'Today + tomorrow' },
          { path: 'today:2', label: 'Today + next 2 days' },
          { path: 'today:7', label: 'Today + next 7 days' },
          { path: 'today:13:readable', label: 'Next 14 days (readable)' },
          { path: 'today:0:is:short', label: 'Short date format' },
        ],
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            ref={importInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImportConfig}
          />
          <Button type="button" variant="outline" size="sm" onClick={handleExportConfig}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => importInputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => reset(defaultValues)}
          >
            <RotateCw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button form="configuration-form" type="submit" disabled={isLoading}>
            {submitLabel}
          </Button>
        </div>
      </div>

      <form id="configuration-form" onSubmit={onFormSubmit} aria-label={submitLabel}>
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList className="flex w-full flex-wrap gap-1">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="prompt">Prompt & Messages</TabsTrigger>
            <TabsTrigger value="tools">Tools</TabsTrigger>
            <TabsTrigger value="transfer">Transfer</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            {!isOpenAISelected && <TabsTrigger value="knowledge">Knowledge</TabsTrigger>}
            <TabsTrigger value="widget">Widget</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">General Settings</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Provider, voice, call window, retries, and phone numbers.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Configuration name"
                  htmlFor="name"
                  error={errors.name?.message}
                  required
                >
                  <FormInput
                    id="name"
                    placeholder="e.g. Night cover escalation"
                    error={!!errors.name}
                    {...register('name')}
                  />
                </FormField>

                <FormField
                  label="AI Provider"
                  htmlFor="provider"
                  error={errors.provider?.message}
                  required
                >
                  <FormSelect
                    id="provider"
                    error={!!errors.provider}
                    {...register('provider', {
                      onChange: (e) => {
                        const newVal = e.target.value;
                        if (newVal === 'openai') {
                          setValue('voice_name', DEFAULT_OPENAI_VOICE);
                          setValue('llm_model', DEFAULT_OPENAI_MODEL);
                        } else {
                          setValue('voice_name', DEFAULT_GEMINI_VOICE);
                          setValue('llm_model', DEFAULT_GEMINI_MODEL);
                        }
                      },
                    })}
                  >
                    {PROVIDER_OPTIONS.map((provider) => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField
                  label="Call Provider"
                  htmlFor="call_provider"
                  error={errors.call_provider?.message}
                  required
                >
                  <FormSelect
                    id="call_provider"
                    error={!!errors.call_provider}
                    {...register('call_provider')}
                  >
                    <option value="twilio">Twilio</option>
                    <option value="telnyx">Telnyx</option>
                  </FormSelect>
                </FormField>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField label="Voice" htmlFor="voice_name" error={errors.voice_name?.message}>
                  <Controller
                    name="voice_name"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        id="voice_name"
                        error={!!errors.voice_name}
                        {...field}
                        value={field.value ?? ''}
                      >
                        {field.value &&
                          !voiceOptions.some((voice) => voice.value === field.value) && (
                            <option value={field.value}>{field.value} (saved voice)</option>
                          )}
                        {voiceOptions.map((voice) => (
                          <option key={voice.value} value={voice.value}>
                            {voice.label} ({voice.description})
                          </option>
                        ))}
                      </FormSelect>
                    )}
                  />
                </FormField>

                <FormField label="LLM Model" htmlFor="llm_model" error={errors.llm_model?.message}>
                  <Controller
                    name="llm_model"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        id="llm_model"
                        error={!!errors.llm_model}
                        {...field}
                        value={liveModelSelection(
                          field.value ?? '',
                          advancedObject.gemini_live_backend,
                        )}
                        onChange={(event) => {
                          const selection = parseLiveModelSelection(event.target.value);
                          field.onChange(selection.model);
                          setAdvancedField('gemini_live_backend', selection.backend);
                        }}
                      >
                        {modelOptions.map((model) => (
                          <option key={model.value} value={model.value}>
                            {model.label} {'description' in model ? `(${model.description})` : ''}
                          </option>
                        ))}
                      </FormSelect>
                    )}
                  />
                </FormField>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Text Messaging LLM Model"
                  htmlFor="text_llm_model"
                  error={errors.text_llm_model?.message}
                >
                  <FormInput
                    id="text_llm_model"
                    placeholder="gemini-2.5-flash (default if empty)"
                    error={!!errors.text_llm_model}
                    {...register('text_llm_model')}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    Model for WhatsApp text conversations. Defaults to gemini-2.5-flash if empty.
                  </p>
                </FormField>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Call window start"
                  htmlFor="call_window_start"
                  error={errors.call_window_start?.message}
                  required
                >
                  <FormInput
                    id="call_window_start"
                    type="time"
                    error={!!errors.call_window_start}
                    {...register('call_window_start')}
                  />
                </FormField>

                <FormField
                  label="Call window end"
                  htmlFor="call_window_end"
                  error={errors.call_window_end?.message}
                  required
                >
                  <FormInput
                    id="call_window_end"
                    type="time"
                    error={!!errors.call_window_end}
                    {...register('call_window_end')}
                  />
                </FormField>
              </div>

              {isLoadingTwilioNumbers ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="bg-muted h-4 w-40 animate-pulse rounded" />
                    <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
                  </div>
                  <div className="space-y-2">
                    <div className="bg-muted h-4 w-40 animate-pulse rounded" />
                    <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
                  </div>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {selectedCallProvider === 'telnyx' ? (
                    <FormField
                      label="Inbound Telnyx Number"
                      htmlFor="inbound_telnyx_phone_number_id"
                      error={errors.inbound_telnyx_phone_number_id?.message}
                    >
                      <FormSelect
                        id="inbound_telnyx_phone_number_id"
                        error={!!errors.inbound_telnyx_phone_number_id}
                        {...register('inbound_telnyx_phone_number_id')}
                      >
                        <option value="">Unassigned (uses INBOUND_CONFIG fallback)</option>
                        {telnyxNumbers.map((number) => (
                          <option key={number.id} value={number.id}>
                            {number.friendly_name ? `${number.friendly_name} - ` : ''}
                            {number.phone_number}
                          </option>
                        ))}
                      </FormSelect>
                    </FormField>
                  ) : (
                    <FormField
                      label="Inbound Twilio Number"
                      htmlFor="inbound_twilio_phone_number_id"
                      error={errors.inbound_twilio_phone_number_id?.message}
                    >
                      <FormSelect
                        id="inbound_twilio_phone_number_id"
                        error={!!errors.inbound_twilio_phone_number_id}
                        {...register('inbound_twilio_phone_number_id')}
                      >
                        <option value="">Unassigned (legacy fallback routing)</option>
                        {twilioNumbers.map((number) => (
                          <option key={number.id} value={number.id}>
                            {number.friendly_name ? `${number.friendly_name} - ` : ''}
                            {number.phone_number}
                          </option>
                        ))}
                      </FormSelect>
                    </FormField>
                  )}

                  {selectedCallProvider === 'telnyx' ? (
                    <FormField
                      label="Default Outbound Number"
                      htmlFor="default_outbound_telnyx_phone_number_id"
                      error={errors.default_outbound_telnyx_phone_number_id?.message}
                    >
                      <FormSelect
                        id="default_outbound_telnyx_phone_number_id"
                        error={!!errors.default_outbound_telnyx_phone_number_id}
                        {...register('default_outbound_telnyx_phone_number_id')}
                      >
                        <option value="">Use global TELNYX_PHONE_NUMBER</option>
                        {telnyxNumbers.map((number) => (
                          <option key={number.id} value={number.id}>
                            {number.friendly_name ? `${number.friendly_name} - ` : ''}
                            {number.phone_number}
                          </option>
                        ))}
                      </FormSelect>
                    </FormField>
                  ) : (
                    <FormField
                      label="Default Outbound Number"
                      htmlFor="default_outbound_twilio_phone_number_id"
                      error={errors.default_outbound_twilio_phone_number_id?.message}
                    >
                      <FormSelect
                        id="default_outbound_twilio_phone_number_id"
                        error={!!errors.default_outbound_twilio_phone_number_id}
                        {...register('default_outbound_twilio_phone_number_id')}
                      >
                        <option value="">Use global default number</option>
                        {twilioNumbers.map((number) => (
                          <option key={number.id} value={number.id}>
                            {number.friendly_name ? `${number.friendly_name} - ` : ''}
                            {number.phone_number}
                          </option>
                        ))}
                      </FormSelect>
                    </FormField>
                  )}
                </div>
              )}

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Post-Call Workflow"
                  htmlFor="workflow_id"
                  error={errors.workflow_id?.message}
                >
                  <Controller
                    name="workflow_id"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        id="workflow_id"
                        error={!!errors.workflow_id}
                        {...field}
                        value={field.value ?? ''}
                      >
                        <option value="">None (use system default)</option>
                        {renderWorkflowOptions(field.value, true)}
                      </FormSelect>
                    )}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    Workflow to run after each call ends (review, email, SMS, etc.)
                  </p>
                </FormField>

                <FormField
                  label="Messaging Workflow"
                  htmlFor="messaging_workflow_id"
                  error={errors.messaging_workflow_id?.message}
                >
                  <Controller
                    name="messaging_workflow_id"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        id="messaging_workflow_id"
                        error={!!errors.messaging_workflow_id}
                        {...field}
                        value={field.value ?? ''}
                      >
                        <option value="">None (use post-call workflow)</option>
                        {renderWorkflowOptions(field.value, true)}
                      </FormSelect>
                    )}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    Separate workflow for WhatsApp/messaging sessions. Falls back to post-call
                    workflow if not set.
                  </p>
                </FormField>

                <FormField
                  label="Session Start Workflow"
                  htmlFor="session_start_workflow_id"
                  error={errors.session_start_workflow_id?.message}
                >
                  <Controller
                    name="session_start_workflow_id"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        id="session_start_workflow_id"
                        error={!!errors.session_start_workflow_id}
                        {...field}
                        value={field.value ?? ''}
                      >
                        <option value="">None</option>
                        {renderWorkflowOptions(field.value, false)}
                      </FormSelect>
                    )}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    Runs once when a new session starts on any channel (e.g. pause the dialler).
                  </p>
                </FormField>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Max call duration (minutes)"
                  htmlFor="max_call_duration_minutes"
                  error={errors.max_call_duration_minutes?.message}
                  required
                >
                  <FormInput
                    id="max_call_duration_minutes"
                    type="number"
                    min={1}
                    max={20}
                    error={!!errors.max_call_duration_minutes}
                    {...register('max_call_duration_minutes', { valueAsNumber: true })}
                  />
                </FormField>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  label="Retry limit"
                  htmlFor="retry_limit"
                  error={errors.retry_limit?.message}
                  required
                >
                  <FormInput
                    id="retry_limit"
                    type="number"
                    min={0}
                    max={10}
                    error={!!errors.retry_limit}
                    {...register('retry_limit', { valueAsNumber: true })}
                  />
                </FormField>

                <FormField
                  label="Delay (minutes)"
                  htmlFor="retry_delay_minutes"
                  error={errors.retry_delay_minutes?.message}
                  required
                >
                  <FormInput
                    id="retry_delay_minutes"
                    type="number"
                    min={1}
                    max={240}
                    error={!!errors.retry_delay_minutes}
                    {...register('retry_delay_minutes', { valueAsNumber: true })}
                  />
                </FormField>
              </div>
            </section>

            <section className="bg-card mt-4 space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">Dashboard access</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Key the external Clients Dashboard sends as{' '}
                  <code className="bg-muted rounded px-1 text-xs">x-api-key</code> to read this
                  configuration&apos;s data.
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="dashboard_api_key">
                  Dashboard API Key
                </label>
                <FormInput
                  id="dashboard_api_key"
                  placeholder={
                    dashboardMasterKeySet ? 'Managed globally in Settings' : 'e.g. dk_live_…'
                  }
                  disabled={dashboardMasterKeySet}
                  error={!!errors.dashboard_api_key}
                  {...register('dashboard_api_key')}
                />
                {errors.dashboard_api_key?.message && (
                  <p className="text-destructive text-xs">{errors.dashboard_api_key.message}</p>
                )}
                <p className="text-muted-foreground text-[11px]">
                  {dashboardMasterKeySet
                    ? 'A global Dashboard API Key is set in Settings, which grants access to all configurations. Clear it there to use a per-configuration key.'
                    : "Per-configuration key — grants the dashboard access to only this configuration's data. Leave empty if the client has no dashboard access."}
                </p>
              </div>
            </section>
          </TabsContent>

          {/* Prompt & Messages Tab */}
          <TabsContent value="prompt" className="space-y-4">
            {multiAgentEnabled && agentGraph && (
              <ConversationFlowPanel
                key={JSON.stringify(agentGraph.conversation_flow)}
                graph={agentGraph}
                onChange={(graph) =>
                  setValue('agent_graph', graph, { shouldDirty: true, shouldValidate: true })
                }
              />
            )}
            <MultiAgentSection
              configurationId={initialValues?.id}
              multiAgentEnabled={multiAgentEnabled ?? false}
              onMultiAgentEnabledChange={(enabled) =>
                setValue('multi_agent_enabled', enabled, { shouldDirty: true })
              }
              agentGraph={agentGraph}
              onAgentGraphChange={(graph) => setValue('agent_graph', graph, { shouldDirty: true })}
              transferTargets={transferTargets as TransferTarget[]}
              customTools={customTools}
            />

            {multiAgentEnabled && (
              <MultiAgentSection
                configurationId={initialValues?.id}
                multiAgentEnabled={multiAgentEnabled}
                onMultiAgentEnabledChange={() => {}}
                agentGraph={textAgentGraph}
                onAgentGraphChange={(graph) =>
                  setValue('text_agent_graph', graph, { shouldDirty: true })
                }
                transferTargets={transferTargets as TransferTarget[]}
                customTools={customTools}
                title="Text Multi-Agent Mode (Messaging)"
                description="Separate agent graph for WhatsApp/text messaging sessions. If empty, the voice agent graph above is used as fallback."
                editorTitle="Text Multi-Agent Graph Editor"
                hideToggle
              />
            )}

            <section className="bg-card space-y-6 rounded-xl border p-5">
              <div className="flex flex-col gap-3 border-b pb-3 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <h3 className="text-lg font-semibold tracking-tight">
                    Content & Prompt Versions
                  </h3>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Manage versioned prompt and messaging content.
                  </p>
                </div>
                {initialValues?.id && (
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground flex items-center gap-1 text-xs font-medium">
                        <History className="h-3 w-3" /> Select Version:
                      </span>
                      <Select
                        value={selectedVersionId || ''}
                        onValueChange={(val) => setSelectedVersionId(val)}
                      >
                        <SelectTrigger className="h-8 w-47.5 text-xs">
                          <SelectValue placeholder="Select version" />
                        </SelectTrigger>
                        <SelectContent>
                          {versions.map((v) => (
                            <SelectItem key={v.id} value={v.id} className="text-xs">
                              Version {v.version_number}{' '}
                              {v.id === initialValues?.active_version_id ? '(Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-border mx-1 hidden h-4 w-px lg:block" />

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 text-xs font-medium"
                      onClick={handleCreateVersion}
                      disabled={createVersionMutation.isPending}
                    >
                      <Plus className="h-3.5 w-3.5" /> New Version
                    </Button>

                    {selectedVersionId &&
                      selectedVersionId !== initialValues?.active_version_id && (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 gap-1.5 text-xs font-medium"
                          onClick={handleSetDefault}
                          disabled={setActiveVersionMutation.isPending}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Set as default
                        </Button>
                      )}

                    {selectedVersionId &&
                      selectedVersionId !== initialValues?.active_version_id && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive h-8 px-2"
                          onClick={() => selectedVersion && handleDeleteVersion(selectedVersion)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                  </div>
                )}
              </div>

              {!initialValues?.id && (
                <div className="bg-primary/5 border-primary/10 flex gap-3 rounded-lg border p-4">
                  <Badge variant="secondary" className="h-fit">
                    Initial Version
                  </Badge>
                  <p className="text-muted-foreground text-sm">
                    You are creating the first version of this configuration. You can add more
                    versions later after creating it.
                  </p>
                </div>
              )}

              <div className="space-y-6">
                <FormField
                  label="Welcome message"
                  htmlFor="welcome_message"
                  error={errors.welcome_message?.message}
                >
                  <FormTextarea
                    id="welcome_message"
                    rows={3}
                    placeholder={getWelcomeMessagePlaceholder()}
                    error={!!errors.welcome_message}
                    {...register('welcome_message')}
                  />
                </FormField>

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    label="Missed call SMS"
                    htmlFor="missed_call_sms_message"
                    error={errors.missed_call_sms_message?.message}
                  >
                    <FormTextarea
                      id="missed_call_sms_message"
                      rows={3}
                      placeholder="Message when attempts exhausted..."
                      error={!!errors.missed_call_sms_message}
                      {...register('missed_call_sms_message')}
                    />
                  </FormField>

                  <FormField
                    label="Follow-up SMS"
                    htmlFor="followup_sms_message"
                    error={errors.followup_sms_message?.message}
                  >
                    <FormTextarea
                      id="followup_sms_message"
                      rows={3}
                      placeholder="Message after successful call..."
                      error={!!errors.followup_sms_message}
                      {...register('followup_sms_message')}
                    />
                  </FormField>
                </div>

                <FormField
                  label="Prompt Rules"
                  htmlFor="prompt_rules"
                  description="Persistent rules prepended to every prompt version. Use for rules that should always apply."
                  error={errors.prompt_rules?.message}
                >
                  <FormTextarea
                    id="prompt_rules"
                    rows={4}
                    placeholder="e.g. Always be polite and professional. Never discuss competitor pricing."
                    error={!!errors.prompt_rules}
                    {...register('prompt_rules')}
                  />
                </FormField>

                {multiAgentEnabled ? (
                  <p className="text-muted-foreground rounded-md border border-dashed px-4 py-3 text-sm">
                    Prompt is managed per-agent in the graph editor above.
                  </p>
                ) : (
                  <FormField
                    label="Prompt template"
                    htmlFor="prompt_template"
                    error={errors.prompt_template?.message}
                    required
                  >
                    <div
                      className="border-input hover:border-primary/50 min-h-[200px] cursor-pointer rounded-md border p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap transition-colors"
                      onClick={() => setPromptEditorOpen(true)}
                    >
                      {promptTemplate ? (
                        <pre className="max-h-[300px] overflow-hidden text-sm">
                          {promptTemplate}
                        </pre>
                      ) : (
                        <span className="text-muted-foreground">
                          Click to open the prompt editor...
                        </span>
                      )}
                    </div>
                    {errors.prompt_template && (
                      <p className="text-destructive mt-1 text-xs">
                        {errors.prompt_template.message}
                      </p>
                    )}
                  </FormField>
                )}

                <FormField
                  label="Text Prompt Template (Messaging)"
                  htmlFor="text_prompt_template"
                  error={errors.text_prompt_template?.message}
                  description="Used for WhatsApp/text messaging. If empty, the voice prompt template is used as fallback."
                >
                  <div
                    className="border-input hover:border-primary/50 min-h-[120px] cursor-pointer rounded-md border p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap transition-colors"
                    onClick={() => setTextPromptEditorOpen(true)}
                  >
                    {textPromptTemplate ? (
                      <pre className="max-h-[200px] overflow-hidden text-sm">
                        {textPromptTemplate}
                      </pre>
                    ) : (
                      <span className="text-muted-foreground">
                        Click to open the text prompt editor...
                      </span>
                    )}
                  </div>
                  {errors.text_prompt_template && (
                    <p className="text-destructive mt-1 text-xs">
                      {errors.text_prompt_template.message}
                    </p>
                  )}
                </FormField>

                <div className="border-t pt-6">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      {...register('notify_on_exhausted_attempts')}
                      className="border-input text-primary h-4 w-4 rounded"
                    />
                    <span className="text-sm font-medium">Notify on exhausted attempts</span>
                  </label>
                </div>
              </div>
            </section>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">Tools & Capabilities</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Enable built-in and custom tools for the AI agent.
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-muted-foreground text-xs font-semibold uppercase">
                  Built-in Tools
                </p>
                <p className="text-muted-foreground text-[11px]">{selectedTools.length} selected</p>
                {tools.map((tool) => {
                  const isChecked = selectedTools.includes(tool.value);
                  return (
                    <div key={tool.value} className="bg-background/60 rounded-md border p-3">
                      <label className="group flex cursor-pointer items-start gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleToolToggle(tool.value, e.target.checked)}
                          className="border-input text-primary focus:ring-primary mt-0.5 h-4 w-4 rounded"
                        />
                        <div className="flex-1 overflow-hidden">
                          <span className="block text-sm font-medium">{tool.label}</span>
                          <p className="text-muted-foreground text-[11px]">{tool.description}</p>
                        </div>
                      </label>

                      {tool.value === FINANCE_LINK_TOOL && isChecked && (
                        <div className="mt-3 space-y-3 border-t pt-3">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              {...register('finance_link_display.enabled')}
                              className="h-4 w-4"
                            />
                            Show the finance link in the web widget
                          </label>

                          {financeDisplayEnabled && (
                            <div className="space-y-3">
                              <p className="text-muted-foreground text-[11px]">
                                When no phone number is provided in a web chat, the finance link is
                                shown on screen instead of texted. The tool returns a{' '}
                                <code>links</code> array with <code>title</code>, <code>url</code>,
                                and <code>description</code> fields you can map below.
                              </p>

                              <div className="grid gap-3 md:grid-cols-2">
                                <FormField label="Display Type" htmlFor="finance_display_type">
                                  <FormSelect
                                    id="finance_display_type"
                                    {...register('finance_link_display.type')}
                                  >
                                    <option value="links">Links</option>
                                    <option value="cards">Cards</option>
                                    <option value="carousel">Carousel</option>
                                    <option value="badges">Badges</option>
                                    <option value="notice">Notice</option>
                                  </FormSelect>
                                </FormField>

                                {financeDisplayType !== 'notice' && (
                                  <FormField
                                    label="Array Path"
                                    htmlFor="finance_display_array_path"
                                  >
                                    <FormInput
                                      id="finance_display_array_path"
                                      placeholder="links"
                                      {...register('finance_link_display.array_path')}
                                      className="text-sm"
                                    />
                                  </FormField>
                                )}
                              </div>

                              <div className="grid gap-3 md:grid-cols-2">
                                <FormField label="Title" htmlFor="finance_display_title" required>
                                  <FormInput
                                    id="finance_display_title"
                                    placeholder="title"
                                    {...register('finance_link_display.title')}
                                    className="text-sm"
                                  />
                                </FormField>

                                <FormField label="Link" htmlFor="finance_display_link">
                                  <FormInput
                                    id="finance_display_link"
                                    placeholder="url"
                                    {...register('finance_link_display.link')}
                                    className="text-sm"
                                  />
                                </FormField>

                                <FormField
                                  label="Description"
                                  htmlFor="finance_display_description"
                                >
                                  <FormInput
                                    id="finance_display_description"
                                    placeholder="description"
                                    {...register('finance_link_display.description')}
                                    className="text-sm"
                                  />
                                </FormField>
                              </div>

                              <label className="flex items-start gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  {...register('finance_link_display.fixed')}
                                  className="mt-0.5 h-4 w-4"
                                />
                                <span>
                                  Keep on screen (pin)
                                  <span className="text-muted-foreground block text-[11px]">
                                    Stays visible when other results (e.g. a reservation link)
                                    appear; new results stack beneath it. Only applies in overlay
                                    display mode.
                                  </span>
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs font-semibold uppercase">
                      Custom Tools
                    </p>
                    <p className="text-muted-foreground mt-1 text-[11px]">
                      {selectedCustomToolIds.length} selected
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[11px]"
                    onClick={handleAddTool}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Add
                  </Button>
                </div>
                {customTools.length === 0 ? (
                  <p className="text-muted-foreground text-xs italic">No custom tools</p>
                ) : (
                  customTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="group bg-background/60 flex items-center justify-between rounded-md border p-3"
                    >
                      <label className="flex flex-1 cursor-pointer items-center gap-2 overflow-hidden">
                        <input
                          type="checkbox"
                          checked={selectedCustomToolIds.includes(tool.id)}
                          onChange={(e) => handleCustomToolToggle(tool.id, e.target.checked)}
                          className="border-input text-primary h-4 w-4 rounded"
                        />
                        <span className="truncate text-sm">{tool.name}</span>
                      </label>
                      <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleEditTool(tool)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive h-6 w-6"
                          onClick={() => handleDeleteTool(tool.id, tool.name)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {(orphanedBuiltinTools.length > 0 || orphanedCustomToolIds.length > 0) && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    <p className="text-xs font-semibold text-amber-600 uppercase dark:text-amber-400">
                      Unknown Tools
                    </p>
                  </div>
                  <p className="text-muted-foreground text-[11px]">
                    These tools are selected in this configuration but don&apos;t exist in the
                    system. They may have been imported from another instance or deleted since.
                  </p>
                  {orphanedBuiltinTools.map((toolValue) => (
                    <div
                      key={toolValue}
                      className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20"
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          built-in
                        </Badge>
                        <span className="truncate font-mono text-sm">{toolValue}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive h-7 w-7 shrink-0"
                        onClick={() => handleToolToggle(toolValue, false)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  {orphanedCustomToolIds.map((toolId) => (
                    <div
                      key={toolId}
                      className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20"
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          custom
                        </Badge>
                        <span className="truncate font-mono text-sm">{toolId}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive h-7 w-7 shrink-0"
                        onClick={() => handleCustomToolToggle(toolId, false)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </TabsContent>

          {/* Transfer Tab */}
          <TabsContent value="transfer">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">Transfer Configuration</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Configure human transfer categories and schedule.
                </p>
              </div>

              {!selectedTools.includes('transfer_to_human') && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Enable the <strong>Transfer to Human</strong> tool in the Tools tab to use
                    transfer functionality.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-xs font-semibold uppercase">
                    Transfer Categories
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-[11px]"
                    onClick={handleAddTransferTarget}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Add category
                  </Button>
                </div>

                {typeof errors.transfer_targets?.message === 'string' && (
                  <p className="text-destructive text-xs">{errors.transfer_targets.message}</p>
                )}

                {transferTargets.length === 0 ? (
                  <p className="text-muted-foreground text-xs italic">
                    No transfer categories configured. Add at least one category.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {transferTargetFields.map((field, index) => {
                      const target = transferTargets[index] ?? field;

                      return (
                        <div key={field.id} className="bg-background rounded-md border p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-xs font-medium">Category {index + 1}</p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive h-6 w-6"
                              onClick={() => handleRemoveTransferTarget(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>

                          <div className="grid gap-3 md:grid-cols-2">
                            <FormField
                              label="Category key"
                              htmlFor={`transfer_targets.${index}.category_key`}
                              error={errors.transfer_targets?.[index]?.category_key?.message}
                              description="snake_case key used by AI (e.g., finance_sales)"
                              required
                            >
                              <FormInput
                                id={`transfer_targets.${index}.category_key`}
                                placeholder="finance_sales"
                                error={!!errors.transfer_targets?.[index]?.category_key}
                                {...register(`transfer_targets.${index}.category_key` as const)}
                              />
                            </FormField>

                            <FormField
                              label="Transfer number"
                              htmlFor={`transfer_targets.${index}.phone_number`}
                              error={errors.transfer_targets?.[index]?.phone_number?.message}
                              description="E.164 format (e.g., +44...)"
                              required
                            >
                              <FormInput
                                id={`transfer_targets.${index}.phone_number`}
                                placeholder="+44..."
                                error={!!errors.transfer_targets?.[index]?.phone_number}
                                {...register(`transfer_targets.${index}.phone_number` as const)}
                              />
                            </FormField>

                            <FormField
                              label="Category description"
                              htmlFor={`transfer_targets.${index}.category_description`}
                              error={
                                errors.transfer_targets?.[index]?.category_description?.message
                              }
                              description="What this category means (used for AI routing decisions)"
                              required
                            >
                              <FormInput
                                id={`transfer_targets.${index}.category_description`}
                                placeholder="Customer wants to discuss finance options"
                                error={!!errors.transfer_targets?.[index]?.category_description}
                                {...register(
                                  `transfer_targets.${index}.category_description` as const,
                                )}
                              />
                            </FormField>

                            <FormField
                              label="Notification emails"
                              htmlFor={`transfer_targets.${index}.notify_emails`}
                              description="Comma-separated emails to notify via extension on transfer"
                            >
                              <FormInput
                                id={`transfer_targets.${index}.notify_emails`}
                                placeholder="alice@company.com, bob@company.com"
                                defaultValue={(transferTargets[index]?.notify_emails ?? []).join(
                                  ', ',
                                )}
                                onChange={(e) => {
                                  const emails = e.target.value
                                    .split(',')
                                    .map((s: string) => s.trim())
                                    .filter(Boolean);
                                  setValue(`transfer_targets.${index}.notify_emails`, emails, {
                                    shouldDirty: true,
                                  });
                                }}
                              />
                            </FormField>

                            <div className="space-y-2 pt-1">
                              <label className="flex items-center gap-2 text-xs font-medium">
                                <input
                                  type="checkbox"
                                  className="border-input text-primary h-4 w-4 rounded"
                                  {...register(`transfer_targets.${index}.sms_enabled` as const)}
                                />
                                Send SMS handoff summary to this number
                              </label>

                              <label className="flex items-center gap-2 text-xs font-medium">
                                <input
                                  type="checkbox"
                                  className="border-input text-primary h-4 w-4 rounded"
                                  checked={Boolean(target.is_default)}
                                  onChange={(e) =>
                                    handleDefaultTransferTargetToggle(index, e.target.checked)
                                  }
                                />
                                Use as default if no category matches
                              </label>

                              <label className="flex items-center gap-2 text-xs font-medium">
                                <input
                                  type="checkbox"
                                  className="border-input text-primary h-4 w-4 rounded"
                                  {...register(`transfer_targets.${index}.active` as const)}
                                />
                                Active
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <FormField
                  label="Legacy fallback transfer number"
                  htmlFor="transfer_phone_number"
                  error={errors.transfer_phone_number?.message}
                  description="Optional fallback used when no category can be matched"
                >
                  <FormInput
                    id="transfer_phone_number"
                    placeholder="+44..."
                    error={!!errors.transfer_phone_number}
                    {...register('transfer_phone_number')}
                  />
                </FormField>

                <TransferScheduleEditor
                  transferSchedule={transferSchedule}
                  register={register}
                  errors={errors}
                  onApplyPreset={handleApplyTransferSchedulePreset}
                  onToggleAllDay={handleToggleTransferScheduleAllDay}
                />
              </div>
            </section>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">
                  Advanced Runtime Configuration
                </h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Fine-tune model parameters, VAD, and runtime settings.
                </p>
              </div>

              <div className="bg-background inline-flex rounded-md border p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={advancedEditorMode === 'form' ? 'default' : 'ghost'}
                  onClick={() => setAdvancedEditorMode('form')}
                  className="h-8"
                >
                  Form
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={advancedEditorMode === 'json' ? 'default' : 'ghost'}
                  onClick={() => setAdvancedEditorMode('json')}
                  className="h-8"
                >
                  JSON
                </Button>
              </div>

              {advancedEditorMode === 'form' ? (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    {!isGeminiNativeAudioSelected && (
                      <FormField label="Language code" htmlFor="advanced_language_code">
                        <FormInput
                          id="advanced_language_code"
                          placeholder="en-GB"
                          value={String(advancedObject.language_code ?? '')}
                          onChange={(e) => setAdvancedField('language_code', e.target.value)}
                        />
                      </FormField>
                    )}

                    {!isOpenAISelected &&
                      selectedModel !== GEMINI_38_LIVE &&
                      (isGeminiThinkingSelected ? (
                        <FormField label="Thinking level" htmlFor="advanced_thinking_level">
                          <FormSelect
                            id="advanced_thinking_level"
                            value={selectedThinkingLevel}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setAdvancedField('thinking_level', raw === '' ? undefined : raw);
                              setAdvancedField('thinking_budget', undefined);
                            }}
                          >
                            {thinkingLevelOptions.map((level) => (
                              <option key={level.value} value={level.value}>
                                {level.label}
                              </option>
                            ))}
                          </FormSelect>
                        </FormField>
                      ) : (
                        <FormField label="Thinking budget" htmlFor="advanced_thinking_budget">
                          <FormInput
                            id="advanced_thinking_budget"
                            type="number"
                            min={0}
                            value={
                              advancedObject.thinking_budget === undefined
                                ? ''
                                : String(advancedObject.thinking_budget)
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              setAdvancedField('thinking_level', undefined);
                              setAdvancedField(
                                'thinking_budget',
                                raw === '' ? undefined : Number.parseInt(raw, 10),
                              );
                            }}
                          />
                        </FormField>
                      ))}

                    {!isOpenAISelected && (
                      <FormField label="Voice engine" htmlFor="advanced_voice_engine">
                        <FormSelect
                          id="advanced_voice_engine"
                          value={String(advancedObject.voice_engine ?? '')}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField('voice_engine', raw === '' ? undefined : raw);
                          }}
                        >
                          <option value="">Live API — single session (default)</option>
                          <option value="adk">ADK runner — session per agent (experimental)</option>
                        </FormSelect>
                      </FormField>
                    )}

                    {isOpenAISelected && (
                      <FormField label="Backend model" htmlFor="advanced_openai_backend_model">
                        <FormSelect
                          id="advanced_openai_backend_model"
                          value={String(advancedObject.openai_backend_model ?? '')}
                          onChange={(e) =>
                            setAdvancedField(
                              'openai_backend_model',
                              e.target.value === '' ? undefined : e.target.value,
                            )
                          }
                        >
                          <option value="">Default (gpt-5.6-terra)</option>
                          {OPENAI_BACKEND_MODEL_OPTIONS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </FormSelect>
                      </FormField>
                    )}

                    {isOpenAISelected && (
                      <FormField
                        label="Backend reasoning"
                        htmlFor="advanced_openai_backend_reasoning"
                      >
                        <FormSelect
                          id="advanced_openai_backend_reasoning"
                          value={String(advancedObject.openai_backend_reasoning ?? '')}
                          onChange={(e) =>
                            setAdvancedField(
                              'openai_backend_reasoning',
                              e.target.value === '' ? undefined : e.target.value,
                            )
                          }
                        >
                          <option value="">Default (minimal)</option>
                          {OPENAI_BACKEND_REASONING_OPTIONS.map((level) => (
                            <option key={level} value={level}>
                              {level}
                            </option>
                          ))}
                        </FormSelect>
                      </FormField>
                    )}

                    {isGemini38Selected && (
                      <FormField
                        label="Tool execution mode"
                        htmlFor="advanced_tool_execution_behavior"
                      >
                        <FormSelect
                          id="advanced_tool_execution_behavior"
                          aria-describedby="tool_execution_help"
                          disabled={isGemini38ThinkingSelected}
                          value={
                            isGemini38ThinkingSelected
                              ? 'non_blocking'
                              : String(advancedObject.tool_execution_behavior ?? 'non_blocking')
                          }
                          onChange={(e) =>
                            setAdvancedField('tool_execution_behavior', e.target.value)
                          }
                        >
                          <option value="non_blocking">
                            Non-blocking — keep conversing (default)
                          </option>
                          <option value="blocking">Blocking — wait for tool results</option>
                        </FormSelect>
                        <p id="tool_execution_help" className="text-muted-foreground text-xs">
                          {isGemini38ThinkingSelected
                            ? 'Extended Thinking requires non-blocking tools and does not support response scheduling.'
                            : 'Default for session tools, including handoffs, unless overridden below. Confirmation rules still apply.'}
                        </p>
                      </FormField>
                    )}

                    {isGemini38Selected && (
                      <ToolExecutionOverrides
                        tools={executionToolOptions}
                        value={advancedObject.tool_execution_overrides}
                        defaultBehavior={advancedObject.tool_execution_behavior}
                        asyncOnly={isGemini38ThinkingSelected}
                        onChange={(value) => setAdvancedField('tool_execution_overrides', value)}
                      />
                    )}

                    {!isGemini35Selected && !isGemini38ThinkingSelected && !isOpenAISelected && (
                      <FormField
                        label="Tool response scheduling"
                        htmlFor="advanced_tool_response_scheduling"
                        description={
                          isGemini38Selected
                            ? 'Applies only to non-blocking tools. Blocking tool results never use scheduling.'
                            : undefined
                        }
                      >
                        <FormSelect
                          id="advanced_tool_response_scheduling"
                          value={String(advancedObject.tool_response_scheduling ?? '')}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField(
                              'tool_response_scheduling',
                              raw === '' ? undefined : raw,
                            );
                          }}
                        >
                          <option value="">When idle (default)</option>
                          <option value="when_idle">
                            When idle — finish sentence, then speak result
                          </option>
                          <option value="interrupt">Interrupt — speak result immediately</option>
                          <option value="silent">Silent — wait for next input</option>
                        </FormSelect>
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField label="Temperature" htmlFor="advanced_temperature">
                        <FormInput
                          id="advanced_temperature"
                          type="number"
                          min={0}
                          max={2}
                          step={0.1}
                          value={
                            advancedObject.temperature === undefined
                              ? ''
                              : String(advancedObject.temperature)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = Number.parseFloat(raw);
                            setAdvancedField(
                              'temperature',
                              raw === '' || Number.isNaN(parsed) ? undefined : parsed,
                            );
                          }}
                        />
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField
                        label="VAD start sensitivity"
                        htmlFor="advanced_vad_start_sensitivity"
                      >
                        <FormSelect
                          id="advanced_vad_start_sensitivity"
                          value={String(advancedObject.vad_start_sensitivity ?? '')}
                          onChange={(e) =>
                            setAdvancedField('vad_start_sensitivity', e.target.value)
                          }
                        >
                          <option value="">Default</option>
                          <option value="START_SENSITIVITY_LOW">Low</option>
                          <option value="START_SENSITIVITY_MEDIUM">Medium</option>
                          <option value="START_SENSITIVITY_HIGH">High</option>
                        </FormSelect>
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField label="VAD end sensitivity" htmlFor="advanced_vad_end_sensitivity">
                        <FormSelect
                          id="advanced_vad_end_sensitivity"
                          value={String(advancedObject.vad_end_sensitivity ?? '')}
                          onChange={(e) => setAdvancedField('vad_end_sensitivity', e.target.value)}
                        >
                          <option value="">Default</option>
                          <option value="END_SENSITIVITY_LOW">Low</option>
                          <option value="END_SENSITIVITY_MEDIUM">Medium</option>
                          <option value="END_SENSITIVITY_HIGH">High</option>
                        </FormSelect>
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField label="Prefix padding (ms)" htmlFor="advanced_prefix_padding_ms">
                        <FormInput
                          id="advanced_prefix_padding_ms"
                          type="number"
                          min={0}
                          value={
                            advancedObject.prefix_padding_ms === undefined
                              ? ''
                              : String(advancedObject.prefix_padding_ms)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField(
                              'prefix_padding_ms',
                              raw === '' ? undefined : Number.parseInt(raw, 10),
                            );
                          }}
                        />
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField
                        label="Silence duration (ms)"
                        htmlFor="advanced_silence_duration_ms"
                      >
                        <FormInput
                          id="advanced_silence_duration_ms"
                          type="number"
                          min={0}
                          value={
                            advancedObject.silence_duration_ms === undefined
                              ? ''
                              : String(advancedObject.silence_duration_ms)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField(
                              'silence_duration_ms',
                              raw === '' ? undefined : Number.parseInt(raw, 10),
                            );
                          }}
                        />
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField label="Trigger tokens" htmlFor="advanced_trigger_tokens">
                        <FormInput
                          id="advanced_trigger_tokens"
                          type="number"
                          min={1}
                          value={
                            advancedObject.trigger_tokens === undefined
                              ? ''
                              : String(advancedObject.trigger_tokens)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField(
                              'trigger_tokens',
                              raw === '' ? undefined : Number.parseInt(raw, 10),
                            );
                          }}
                        />
                      </FormField>
                    )}

                    {!isOpenAISelected && (
                      <FormField label="Target tokens" htmlFor="advanced_target_tokens">
                        <FormInput
                          id="advanced_target_tokens"
                          type="number"
                          min={1}
                          value={
                            advancedObject.target_tokens === undefined
                              ? ''
                              : String(advancedObject.target_tokens)
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAdvancedField(
                              'target_tokens',
                              raw === '' ? undefined : Number.parseInt(raw, 10),
                            );
                          }}
                        />
                      </FormField>
                    )}
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    {!isOpenAISelected && !isGemini38Selected && (
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          className="border-input text-primary h-4 w-4 rounded"
                          checked={Boolean(advancedObject.include_thoughts)}
                          onChange={(e) => setAdvancedField('include_thoughts', e.target.checked)}
                        />
                        Include thoughts
                      </label>
                    )}
                    {!isOpenAISelected && (
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          className="border-input text-primary h-4 w-4 rounded"
                          checked={Boolean(advancedObject.vad_disabled)}
                          onChange={(e) => setAdvancedField('vad_disabled', e.target.checked)}
                        />
                        Disable VAD
                      </label>
                    )}
                    {!isGeminiThinkingSelected && !isGemini38Selected && !isOpenAISelected && (
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          className="border-input text-primary h-4 w-4 rounded"
                          checked={Boolean(advancedObject.enable_affective_dialog)}
                          onChange={(e) =>
                            setAdvancedField('enable_affective_dialog', e.target.checked)
                          }
                        />
                        Enable affective dialog
                      </label>
                    )}
                    {!isGeminiThinkingSelected && !isGemini38Selected && !isOpenAISelected && (
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          className="border-input text-primary h-4 w-4 rounded"
                          checked={Boolean(advancedObject.proactive_audio)}
                          onChange={(e) => setAdvancedField('proactive_audio', e.target.checked)}
                        />
                        Proactive audio
                      </label>
                    )}
                    {!isOpenAISelected && (
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          className="border-input text-primary h-4 w-4 rounded"
                          checked={Boolean(advancedObject.compression_disabled)}
                          onChange={(e) =>
                            setAdvancedField('compression_disabled', e.target.checked)
                          }
                        />
                        Disable compression
                      </label>
                    )}
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        className="border-input text-primary h-4 w-4 rounded"
                        checked={Boolean(advancedObject.grounding_enabled)}
                        onChange={(e) => setAdvancedField('grounding_enabled', e.target.checked)}
                      />
                      {isOpenAISelected ? 'Web search (backend)' : 'Google Search grounding'}
                    </label>

                    {isGeminiNativeAudioSelected && (
                      <p className="text-muted-foreground text-xs md:col-span-2">
                        Native audio models use automatic language detection and ignore explicit
                        language code.
                      </p>
                    )}

                    {isGemini38Selected && (
                      <p className="text-muted-foreground text-xs md:col-span-2">
                        Gemini 3.8 uses AI Studio and requires a Gemini API key. Proactive audio is
                        always enabled. Both voice engines support single-agent and multi-agent
                        calls.
                        {isGemini38ThinkingSelected
                          ? ' Extended Thinking uses background tools, supports low/medium/high reasoning, and does not support tool response scheduling.'
                          : ' Standard Live does not accept a thinking level or budget.'}
                      </p>
                    )}
                    {isGeminiThinkingSelected && !isGemini38Selected && (
                      <p className="text-muted-foreground text-xs md:col-span-2">
                        Gemini 3.1/3.5 Flash Live models do not support affective dialog or
                        proactive audio.
                        {isGemini35Selected &&
                          ' Gemini 3.5 Flash Live also does not support tool response scheduling.'}
                      </p>
                    )}
                  </div>

                  {/* Silence Breaker */}
                  <div className="border-t pt-4">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        className="border-input text-primary h-4 w-4 rounded"
                        checked={Boolean(
                          (advancedObject.silence_breaker as Record<string, unknown> | undefined)
                            ?.enabled,
                        )}
                        onChange={(e) => {
                          const current =
                            (advancedObject.silence_breaker as Record<string, unknown>) || {};
                          const updated = e.target.checked
                            ? {
                                enabled: true,
                                timeout_ms: (current.timeout_ms as number) || 5000,
                                max_attempts: (current.max_attempts as number) || 3,
                              }
                            : undefined;
                          setAdvancedField(
                            'silence_breaker',
                            updated as Record<string, unknown> | undefined,
                          );
                        }}
                      />
                      Enable silence breaker
                    </label>
                    <p className="text-muted-foreground mt-1 ml-6 text-xs">
                      Nudges the model when no transcript is generated after a turn completes.
                      Timeout adds to the last response duration.
                    </p>

                    {Boolean(
                      (advancedObject.silence_breaker as Record<string, unknown> | undefined)
                        ?.enabled,
                    ) && (
                      <div className="mt-3 ml-6 grid gap-3 md:grid-cols-2">
                        <FormField
                          label="Silence timeout (ms)"
                          htmlFor="advanced_silence_breaker_timeout"
                        >
                          <FormInput
                            id="advanced_silence_breaker_timeout"
                            type="number"
                            min={2000}
                            max={30000}
                            step={1000}
                            value={
                              (
                                advancedObject.silence_breaker as
                                  | Record<string, unknown>
                                  | undefined
                              )?.timeout_ms === undefined
                                ? ''
                                : String(
                                    (advancedObject.silence_breaker as Record<string, unknown>)
                                      ?.timeout_ms,
                                  )
                            }
                            onChange={(e) => {
                              const current =
                                (advancedObject.silence_breaker as Record<string, unknown>) || {};
                              const val = e.target.value ? Number(e.target.value) : undefined;
                              setAdvancedField('silence_breaker', {
                                ...current,
                                timeout_ms: val,
                              });
                            }}
                          />
                        </FormField>

                        <FormField
                          label="Max attempts"
                          htmlFor="advanced_silence_breaker_max_attempts"
                        >
                          <FormInput
                            id="advanced_silence_breaker_max_attempts"
                            type="number"
                            min={1}
                            max={10}
                            value={
                              (
                                advancedObject.silence_breaker as
                                  | Record<string, unknown>
                                  | undefined
                              )?.max_attempts === undefined
                                ? ''
                                : String(
                                    (advancedObject.silence_breaker as Record<string, unknown>)
                                      ?.max_attempts,
                                  )
                            }
                            onChange={(e) => {
                              const current =
                                (advancedObject.silence_breaker as Record<string, unknown>) || {};
                              const val = e.target.value ? Number(e.target.value) : undefined;
                              setAdvancedField('silence_breaker', {
                                ...current,
                                max_attempts: val,
                              });
                            }}
                          />
                        </FormField>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <FormField
                  label="Advanced configuration (JSON)"
                  htmlFor="advanced"
                  error={errors.advanced?.message}
                >
                  <FormTextarea
                    id="advanced"
                    rows={12}
                    className="font-mono text-xs leading-relaxed"
                    placeholder={
                      isGemini38Selected
                        ? JSON.stringify(
                            normalizeAdvancedForModel(
                              { temperature: 0.8, thinking_level: 'low' },
                              'gemini',
                              selectedModel ?? undefined,
                            ),
                            null,
                            2,
                          )
                        : isGeminiThinkingSelected
                          ? ADVANCED_PLACEHOLDER_GEMINI_THINKING
                          : isGeminiNativeAudioSelected
                            ? ADVANCED_PLACEHOLDER_GEMINI_NATIVE
                            : ADVANCED_PLACEHOLDER_DEFAULT
                    }
                    error={!!errors.advanced}
                    {...register('advanced')}
                  />
                </FormField>
              )}
            </section>
          </TabsContent>

          {/* Knowledge (RAG) Tab */}
          {!isOpenAISelected && (
            <TabsContent value="knowledge">
              <RagKnowledgeSection
                configurationId={initialValues?.id}
                ragEnabled={Boolean(advancedObject.rag_enabled)}
                onRagEnabledChange={(value) => setAdvancedField('rag_enabled', value)}
              />
            </TabsContent>
          )}

          {/* Widget Tab */}
          <TabsContent value="widget">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">Widget Embed</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Configure the embeddable voice widget for your website.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Enable Widget</span>
                <Controller
                  control={control}
                  name="widget_enabled"
                  render={({ field }) => (
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  )}
                />
              </div>

              {widgetEnabled && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium">Enable Text Chat</span>
                      <p className="text-muted-foreground text-[11px]">
                        Allow visitors to send text messages and images through the widget.
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name="widget_text_chat_enabled"
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">WhatsApp Numbers</label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddWhatsappNumber}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add number
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-[11px]">
                      One number: the widget shows a WhatsApp icon that opens a chat directly. Two
                      or more: visitors get a menu to pick who to chat with — give each a short
                      label (e.g. &quot;Chat to a Person&quot;, &quot;Chat to AI&quot;).
                    </p>

                    {whatsappNumberFields.length === 0 ? (
                      <p className="text-muted-foreground text-xs italic">
                        No WhatsApp numbers configured.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {whatsappNumberFields.map((field, index) => (
                          <div key={field.id} className="bg-background rounded-md border p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <p className="text-xs font-medium">Number {index + 1}</p>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-destructive h-6 w-6"
                                onClick={() => handleRemoveWhatsappNumber(index)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <FormField
                                label="Number"
                                htmlFor={`whatsapp_numbers.${index}.number`}
                                error={errors.whatsapp_numbers?.[index]?.number?.message}
                                description="E.164 format (e.g., +44...)"
                                required
                              >
                                <FormInput
                                  id={`whatsapp_numbers.${index}.number`}
                                  placeholder="+441234567890"
                                  error={!!errors.whatsapp_numbers?.[index]?.number}
                                  {...register(`whatsapp_numbers.${index}.number` as const)}
                                />
                              </FormField>

                              <FormField
                                label="Label"
                                htmlFor={`whatsapp_numbers.${index}.label`}
                                error={errors.whatsapp_numbers?.[index]?.label?.message}
                                description={
                                  whatsappNumberFields.length > 1
                                    ? 'Shown in the picker (e.g., "Chat to a Person")'
                                    : 'Only shown when a second number is added'
                                }
                              >
                                <FormInput
                                  id={`whatsapp_numbers.${index}.label`}
                                  placeholder="Chat to a Person"
                                  error={!!errors.whatsapp_numbers?.[index]?.label}
                                  {...register(`whatsapp_numbers.${index}.label` as const)}
                                />
                              </FormField>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium">WhatsApp only</span>
                      <p className="text-muted-foreground text-[11px]">
                        Show only the WhatsApp button — no voice &quot;Start Call&quot; or chat
                        panel. Requires a WhatsApp number above.
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name="widget_whatsapp_only"
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>

                  {whatsappOnly && whatsappNumbers.every((entry) => !entry.number?.trim()) && (
                    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      <p className="text-muted-foreground text-xs">
                        WhatsApp-only mode needs a WhatsApp number to have anything to show. Set a
                        number above, or the widget will fall back to the normal voice/chat button.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Allowed Origins</label>
                    <p className="text-muted-foreground text-[11px]">
                      Domains that can embed this widget (e.g., carpass.ai, *.example.com)
                    </p>
                    <Controller
                      control={control}
                      name="widget_allowed_origins"
                      render={({ field }) => (
                        <textarea
                          className="border-input placeholder:text-muted-foreground flex min-h-[60px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                          placeholder={'carpass.ai\n*.example.com'}
                          value={(field.value || []).join('\n')}
                          onChange={(e) => {
                            const origins = e.target.value
                              .split(/[\n,]/)
                              .map((s: string) => s.trim())
                              .filter(Boolean);
                            field.onChange(origins);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium">
                      Rate Limit (calls per hour per visitor)
                    </label>
                    <Controller
                      control={control}
                      name="widget_rate_limit_per_hour"
                      render={({ field }) => (
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                          value={field.value ?? 10}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs font-semibold uppercase">
                      Branding
                    </p>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Title</label>
                      <Controller
                        control={control}
                        name="widget_branding.title"
                        render={({ field }) => (
                          <input
                            type="text"
                            className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            placeholder="Talk to us"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Subtitle</label>
                      <Controller
                        control={control}
                        name="widget_branding.subtitle"
                        render={({ field }) => (
                          <input
                            type="text"
                            className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            placeholder="Voice Assistant"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Logo URL</label>
                      <Controller
                        control={control}
                        name="widget_branding.logo_url"
                        render={({ field }) => (
                          <input
                            type="url"
                            className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            placeholder="https://example.com/logo.png"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Logo Style (CSS)</label>
                      <Controller
                        control={control}
                        name="widget_branding.logo_style"
                        render={({ field }) => (
                          <input
                            type="text"
                            className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 font-mono text-xs"
                            placeholder="filter:brightness(0) saturate(100%);padding:4px"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <p className="text-muted-foreground text-[11px]">
                        Raw inline CSS applied to the logo image (e.g.{' '}
                        <code>filter:brightness(0) saturate(100%)</code>, <code>padding:6px</code>,{' '}
                        <code>mix-blend-mode:multiply</code>).
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Primary Color</label>
                      <Controller
                        control={control}
                        name="widget_branding.primary_color"
                        render={({ field }) => (
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              className="h-10 w-10 cursor-pointer rounded border"
                              value={field.value || '#2563eb'}
                              onChange={field.onChange}
                            />
                            <input
                              type="text"
                              className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                              placeholder="#2563eb"
                              value={field.value ?? ''}
                              onChange={field.onChange}
                            />
                          </div>
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Welcome Message</label>
                      <Controller
                        control={control}
                        name="widget_branding.welcome_message"
                        render={({ field }) => (
                          <textarea
                            className="border-input flex min-h-[60px] w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            placeholder="Hi! Click the button below to speak with our AI assistant."
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium">Tool Display Mode</label>
                      <Controller
                        control={control}
                        name="widget_branding.tool_display_mode"
                        render={({ field }) => (
                          <select
                            className="border-input flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                            value={field.value ?? 'inline'}
                            onChange={field.onChange}
                          >
                            <option value="inline">Inline (in transcript)</option>
                            <option value="overlay">Overlay (expanded view)</option>
                          </select>
                        )}
                      />
                      <p className="text-muted-foreground text-xs">
                        Inline shows tool results inside the chat. Overlay displays them in a larger
                        panel to the left of the widget.
                      </p>
                    </div>
                  </div>

                  <BrandingLivePreview control={control} />

                  <div className="space-y-2">
                    <p className="text-muted-foreground text-xs font-semibold uppercase">
                      Custom CSS
                    </p>
                    <p className="text-muted-foreground text-[11px]">
                      Override any widget style using CSS IDs (e.g., <code>#cw-fab-webchat</code>,{' '}
                      <code>#cw-fab-whatsapp</code>, <code>#cw-panel</code>). Applied after default
                      styles.
                    </p>
                    <Controller
                      control={control}
                      name="widget_custom_css"
                      render={({ field }) => (
                        <textarea
                          className="border-input flex min-h-[120px] w-full rounded-md border bg-transparent px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none"
                          placeholder={`/* Available IDs:\n   #cw-fab-container\n   #cw-fab-webchat\n   #cw-fab-whatsapp\n   #cw-panel\n   #cw-panel-header\n   #cw-panel-status\n   #cw-panel-call-btn\n   #cw-panel-transcript\n   #cw-panel-controls\n   #cw-footer\n   #cw-welcome-bubble\n*/`}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>

                  {initialValues?.id && (
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-xs font-semibold uppercase">
                        Embed Code
                      </p>
                      <div className="relative">
                        <pre className="bg-muted overflow-x-auto rounded-md p-3 text-xs">
                          {`<script src="${getEnv('VITE_API_URL')}/widget/${initialValues.id}/loader">` +
                            '</' +
                            `script>`}
                        </pre>
                        <button
                          type="button"
                          className="bg-background absolute top-2 right-2 rounded border px-2 py-1 text-xs"
                          onClick={() => {
                            void navigator.clipboard.writeText(
                              `<script src="${getEnv('VITE_API_URL')}/widget/${initialValues.id}/loader"></script>`,
                            );
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-muted-foreground text-[11px]">
                        Add this single line to your website to embed the call widget.
                      </p>
                    </div>
                  )}
                </>
              )}
            </section>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <section className="bg-card space-y-5 rounded-xl border p-5">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold tracking-tight">Email Channel</h3>
                <p className="text-muted-foreground mt-1 text-xs">
                  Configure the inbound email address and email template for AI replies.
                </p>
              </div>

              <FormField
                label="Email Connector"
                htmlFor="inbound_email_connector_id"
                description="Select which email connector handles inbound emails for this configuration."
              >
                <Controller
                  control={control}
                  name="inbound_email_connector_id"
                  render={({ field }) => (
                    <FormSelect
                      value={field.value ?? ''}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        const connector = emailConnectors.find(
                          (c: EmailConnector) => c.id === e.target.value,
                        );
                        if (connector?.email_addresses.length === 1) {
                          setValue('inbound_email_address', connector.email_addresses[0]);
                        } else {
                          setValue('inbound_email_address', '');
                        }
                      }}
                    >
                      <option value="">None</option>
                      {emailConnectors
                        .filter((c: EmailConnector) => c.is_active)
                        .map((c: EmailConnector) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.provider_type})
                          </option>
                        ))}
                    </FormSelect>
                  )}
                />
              </FormField>

              {watchedConnectorId && (
                <FormField
                  label="Inbound Email Address"
                  htmlFor="inbound_email_address"
                  error={errors.inbound_email_address?.message}
                  description="The specific email address within the connector that this configuration handles."
                >
                  <Controller
                    control={control}
                    name="inbound_email_address"
                    render={({ field }) => {
                      const connector = emailConnectors.find(
                        (c: EmailConnector) => c.id === watchedConnectorId,
                      );
                      const addresses = connector?.email_addresses ?? [];
                      return (
                        <FormSelect
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <option value="">Select email address...</option>
                          {addresses.map((addr: string) => (
                            <option key={addr} value={addr}>
                              {addr}
                            </option>
                          ))}
                        </FormSelect>
                      );
                    }}
                  />
                </FormField>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Email Template</label>
                    <p className="text-muted-foreground text-xs">
                      HTML wrapper for all outgoing emails. Use{' '}
                      <code className="bg-muted rounded px-1 text-xs">{'{{content}}'}</code> where
                      the AI reply should appear.
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="email_template"
                    render={({ field }) => (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!field.value?.trim()) {
                            field.onChange(DEFAULT_EMAIL_TEMPLATE);
                          }
                        }}
                        disabled={!!field.value?.trim()}
                      >
                        Load Default
                      </Button>
                    )}
                  />
                </div>

                <Controller
                  control={control}
                  name="email_template"
                  render={({ field }) => (
                    <textarea
                      className="border-input flex min-h-[300px] w-full rounded-md border bg-transparent px-3 py-2 font-mono text-xs leading-relaxed focus-visible:ring-2 focus-visible:outline-none"
                      placeholder={DEFAULT_EMAIL_TEMPLATE}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  )}
                />

                {!getValues('email_template')?.includes('{{content}}') &&
                  getValues('email_template')?.trim() && (
                    <p className="text-destructive text-xs">
                      Template must contain {'{{content}}'} placeholder for the email body.
                    </p>
                  )}
              </div>

              {/* Live Preview */}
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs font-semibold uppercase">Preview</p>
                <Controller
                  control={control}
                  name="email_template"
                  render={({ field }) => {
                    const preview = (field.value || DEFAULT_EMAIL_TEMPLATE).replace(
                      '{{content}}',
                      '<p>Hi there,</p><p>This is a sample AI reply to show how your email template will look when sent to customers.</p><p>Best regards,<br>AI Agent</p>',
                    );
                    return (
                      <div className="rounded-lg border bg-white p-1">
                        <iframe
                          srcDoc={preview}
                          title="Email preview"
                          className="h-[400px] w-full rounded border-0"
                          sandbox=""
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </form>

      <TemplateCodeEditor
        value={promptTemplate ?? ''}
        onChange={(val) => setValue('prompt_template', val, { shouldDirty: true })}
        type="sms"
        aiContext="prompt"
        variableGroups={promptVariableGroups}
        open={promptEditorOpen}
        onOpenChange={setPromptEditorOpen}
      />

      <TemplateCodeEditor
        value={textPromptTemplate ?? ''}
        onChange={(val) => setValue('text_prompt_template', val, { shouldDirty: true })}
        type="sms"
        aiContext="prompt"
        variableGroups={promptVariableGroups}
        open={textPromptEditorOpen}
        onOpenChange={setTextPromptEditorOpen}
      />

      {/* Shared Modals */}
      <CustomToolModal
        open={customToolModalState.open}
        onOpenChange={(open) => setCustomToolModalState((prev) => ({ ...prev, open }))}
        mode={customToolModalState.mode}
        initialValues={customToolModalState.initialValues || undefined}
        onSubmit={handleSubmitCustomTool}
        errors={customToolModalErrors}
        isLoading={isSavingCustomTool}
      />

      <DeleteCustomToolDialog
        open={deleteDialogState.open}
        onOpenChange={(open) => setDeleteDialogState((prev) => ({ ...prev, open }))}
        onConfirm={handleConfirmDeleteTool}
        toolName={deleteDialogState.toolName}
        configurations={deleteDialogState.configurations}
        isLoading={deleteCustomToolMutation.isPending}
      />

      <Dialog
        open={Boolean(deleteVersionTarget)}
        onOpenChange={(open) => !open && setDeleteVersionTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-destructive h-5 w-5" />
              Delete Version {deleteVersionTarget?.number}?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. You are about to delete a configuration version.
            </DialogDescription>
            {deleteVersionTarget && deleteVersionTarget.callCount > 0 && (
              <div className="bg-destructive/10 border-destructive/20 text-destructive mt-3 rounded-md border p-3 text-sm font-medium">
                Warning: There are {deleteVersionTarget.callCount} calls linked to this version.
                Deleting it will clear the version reference on these calls.
              </div>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteVersionTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmDeleteVersion} variant="destructive">
              Delete Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
